import React, { useState, useEffect, useCallback, useRef, Component } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Briefcase, Plus, Search, Calendar, DollarSign, Package,
  MapPin, Home, Loader2, RefreshCw, Trash2, X, Download, Link2, Copy,
  FileSpreadsheet, Upload, AlertTriangle, ChevronLeft, Save, FolderOpen, Edit3, Check,
  Camera, Image, Sparkles, CheckCircle, Settings, ChevronDown, Database, Globe,
  Circle as CircleIcon
} from 'lucide-react';

// Error Boundary to prevent blank screens
class CreateJobErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error('CreateJob Error:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Something went wrong</h2>
          <p className="text-gray-600 mb-4">{this.state.error?.message || 'An unexpected error occurred'}</p>
          <button
            onClick={() => { this.setState({ hasError: false, error: null }); window.location.href = '/admin/jobs'; }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Jobs
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Optimized Google Maps component
import DeliveryZoneMap from '../../components/map/DeliveryZoneMap';

// Leaflet for PDF export map only
import { MapContainer, TileLayer, Polygon, Marker, Tooltip, Rectangle, Circle, CircleMarker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Import extracted components and utilities
import {
  houseMarkerIcon,
  createGridLabelIcon,
  centerPointIcon,
  midpointMarkerIcon,
  MapClickHandler,
  MapBoundsFitter,
  CircleBoundsFitter,
  MapCenterUpdater,
  RadiusDrawHandler,
  DELIVERY_TYPES,
  MIN_JOB_VALUE,
  MAX_APARTMENTS_PER_GRID,
  DEFAULT_RADIUS_MILES,
  HOUSING_DENSITY,
  calculateMaxPerGrid,
  calculateCenter,
  calculatePolygonAreaAcres,
  estimateHousesFromArea,
  getCombinedBoundingBox,
  pointInPolygon,
  hashPolygon,
  circleToPolygon,
  metersToMilesRounded
} from '../../components/admin/job-creation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminCreateJob = () => {
  const { getAuthHeader, userType } = useAuth();
  const navigate = useNavigate();
  const isFullAdmin = userType === 'admin';
  
  // Form state
  const [drawingMode, setDrawingMode] = useState(false);
  // ---- Radius Draw button state. The actual radius-draw INTERACTION
  // (center placement, mouse-tracking, preview circle) lives inside
  // <DeliveryZoneMap>, which keeps that high-frequency state internal so
  // this giant parent component doesn't re-render on every mouse pixel.
  // We only track whether the mode is active + whether a center has been
  // dropped (so we can swap the prompt panel). The mile-count display
  // lives on the map itself.
  const [radiusDrawMode, setRadiusDrawMode] = useState(false);
  const [radiusCenterPlaced, setRadiusCenterPlaced] = useState(false);
  // ---- Exclusion Zone draw state. Mirrors the polygon-zone flow but
  // keeps the data in a separate array so excluded houses can be
  // subtracted from the count without touching the delivery polygons.
  //   exclusionPolygons : list of closed exclusion polygons (each a list of [lat,lng])
  //   exclusionDrawingMode : currently drawing a new exclusion?
  //   exclusionPoints      : vertices of the in-progress exclusion polygon
  //   excludedHouseCount   : how many houses fell inside any exclusion (for the count badge)
  const [exclusionPolygons, setExclusionPolygons] = useState([]);
  const [exclusionDrawingMode, setExclusionDrawingMode] = useState(false);
  const [exclusionPoints, setExclusionPoints] = useState([]);
  const [excludedHouseCount, setExcludedHouseCount] = useState(0);
  const [mapCenter, setMapCenter] = useState([29.7604, -95.3698]); // Houston, TX (default with parcel data)
  const [polygons, setPolygons] = useState([[]]); // Array of polygons, each polygon is array of [lat, lng]
  const [activePolygonIndex, setActivePolygonIndex] = useState(0); // Currently active polygon for editing
  const [houseCount, setHouseCount] = useState(null);
  const [addressBreakdown, setAddressBreakdown] = useState(null);
  const [gridSections, setGridSections] = useState([]);
  const [countingHouses, setCountingHouses] = useState(false);
  const [countConfidence, setCountConfidence] = useState(null);
  // Tracks which data source produced the current count: 'parcel_db' | 'osm' | 'manual' | 'ai_verified' | 'google_verified' | 'uploaded' | 'cloned'
  const [dataSource, setDataSource] = useState(null);
  // When checked, Count Houses queries the LEGACY USA parcel data system (5M+ docs in usa_parcels).
  // When UNCHECKED (default), it queries the new clean-slate Parcel Data Mgmt V2 collections.
  const [useLegacyParcelData, setUseLegacyParcelData] = useState(false);
  const [creatingJobs, setCreatingJobs] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [hideGridsForPDF, setHideGridsForPDF] = useState(false);
  
  // Get active polygon's boundary points (for backward compatibility)
  const boundaryPoints = polygons[activePolygonIndex] || [];
  const setBoundaryPoints = (updater) => {
    setPolygons(prev => {
      const newPolygons = [...prev];
      if (typeof updater === 'function') {
        newPolygons[activePolygonIndex] = updater(newPolygons[activePolygonIndex] || []);
      } else {
        newPolygons[activePolygonIndex] = updater;
      }
      return newPolygons;
    });
  };
  
  // Get all boundary points combined (for house counting)
  const getAllBoundaryPoints = () => polygons.filter(p => p.length >= 3);
  
  // Manual house count override
  const [manualHouseCount, setManualHouseCount] = useState('');
  const [useManualCount, setUseManualCount] = useState(false);
  
  // Draft saving
  const [savedDrafts, setSavedDrafts] = useState([]);
  const [savingDraft, setSavingDraft] = useState(false);
  const [showDraftsModal, setShowDraftsModal] = useState(false);
  
  // AI image analysis state
  const [analyzingImage, setAnalyzingImage] = useState(false);
  const [aiAnalysisResult, setAiAnalysisResult] = useState(null);
  const imageInputRef = useRef(null);
  
  // Auto satellite capture state
  const [capturingSatellite, setCapturingSatellite] = useState(false);
  const [satelliteImage, setSatelliteImage] = useState(null);
  
  // Google API search state
  const [searchingGoogleApi, setSearchingGoogleApi] = useState(false);
  const [googleApiResult, setGoogleApiResult] = useState(null);
  
  // Enhanced house search state (uses local parcels + OSM + Google)
  const [searchingEnhanced, setSearchingEnhanced] = useState(false);
  const [enhancedSearchResult, setEnhancedSearchResult] = useState(null);
  
  // USA Parcel Database - "Count Houses" button always queries Parcel DB (no toggle).
  const [parcelDbStatus, setParcelDbStatus] = useState(null);
  
  // Stores the actual house coordinates from the most recent counting method
  const [countedHouseLocations, setCountedHouseLocations] = useState([]);
  
  // House count cache - ensures consistent results for same polygons
  const houseCountCache = useRef(new Map());
  
  const mapContainerRef = useRef(null);
  
  // Circle zone state
  const [circleCenter, setCircleCenter] = useState(null);
  const [circleRadius, setCircleRadius] = useState(DEFAULT_RADIUS_MILES);
  
  // Address search state
  const [addressSearchQuery, setAddressSearchQuery] = useState('');
  const [searchingAddress, setSearchingAddress] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const addressSearchRef = useRef(null);
  const [mapCenterTrigger, setMapCenterTrigger] = useState(0);
  
  // Address specific file upload state
  const [uploadedAddresses, setUploadedAddresses] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  
  // Copy/paste address state
  const [pasteAddressText, setPasteAddressText] = useState('');
  const [parsingPastedAddresses, setParsingPastedAddresses] = useState(false);
  const [geocodingProgress, setGeocodingProgress] = useState(null); // { total, geocoded, failed }
  
  // Canvassing PDF state
  const [canvassingTurfInfo, setCanvassingTurfInfo] = useState(null);
  const [processingPdf, setProcessingPdf] = useState(false);
  
  // Job form data
  const [newJob, setNewJob] = useState({
    title: '',
    client: '',
    client_contact_name: '',
    client_phone: '',
    client_email: '',
    location: '',
    deliveries: 0,
    pay_per_flyer: 0.25,
    bonus_pay: 0,
    deadline: '',
    flyer_type: 'Standard',
    description: '',
    delivery_type: 'houses',
    client_payment: 0,
    client_cost_per_delivery: 0,
    pickup_location: '',
    pickup_date: '',
    pickup_time: ''
  });

  // Load saved drafts on mount
  useEffect(() => {
    const loadDrafts = async () => {
      try {
        const response = await axios.get(`${API}/admin/job-drafts`, getAuthHeader());
        setSavedDrafts(response.data || []);
      } catch (error) {
        console.error('Error loading drafts:', error);
      }
    };
    loadDrafts();
  }, []);

  // Check USA parcel database status on mount
  useEffect(() => {
    const checkParcelDbStatus = async () => {
      try {
        const response = await axios.get(`${API}/admin/usa-parcel-data/status`, getAuthHeader());
        setParcelDbStatus(response.data);
        console.log('Parcel DB status:', response.data?._total);
      } catch (error) {
        console.error('Error checking parcel DB status:', error);
        setParcelDbStatus(null);
      }
    };
    checkParcelDbStatus();
  }, []);

  // Check for clone data on mount
  useEffect(() => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('clone') !== 'true') {
        // Not in clone mode - clear any stale clone data
        sessionStorage.removeItem('cloneJobData');
        return;
      }
      
      const cloneDataStr = sessionStorage.getItem('cloneJobData');
      console.log('Clone mode detected, cloneDataStr:', cloneDataStr ? 'found' : 'not found');
      
      if (!cloneDataStr) {
        console.warn('Clone mode but no clone data found');
        window.history.replaceState({}, '', '/admin/create-job');
        return;
      }
      
      const cloneData = JSON.parse(cloneDataStr);
      console.log('Clone data parsed:', {
        id: cloneData.id,
        title: cloneData.job_title || cloneData.title,
        client: cloneData.client,
        deliveries: cloneData.deliveries,
        hasDeliveryBoundary: cloneData.delivery_boundary?.length >= 3,
        hasPolygons: cloneData.delivery_polygons?.length > 0,
        hasCircle: cloneData.circle_center?.length === 2
      });
          
          // Set job form data with all required fields
          setNewJob({
            title: cloneData.title || cloneData.job_title || '',
            client: cloneData.client || '',
            client_contact_name: cloneData.client_contact_name || '',
            client_phone: cloneData.client_phone || '',
            client_email: cloneData.client_email || '',
            delivery_type: cloneData.delivery_type || 'houses',
            pay_per_flyer: cloneData.pay_per_flyer || 0.25,
            bonus_pay: cloneData.bonus_pay || 0,
            deadline: '',
            flyer_type: cloneData.flyer_type || 'Standard',
            description: cloneData.description || '',
            deliveries: cloneData.deliveries || 0,
            client_payment: cloneData.client_payment || 0,
            client_cost_per_delivery: cloneData.client_cost_per_delivery || 0,
            pickup_location: cloneData.pickup_location || '',
            pickup_date: '',
            pickup_time: cloneData.pickup_time || ''
          });
          
          // Set map center if available
          if (cloneData.map_center && cloneData.map_center.length === 2) {
            setMapCenter(cloneData.map_center);
          }
          
          // Handle multi-polygon zones (delivery_polygons)
          if (cloneData.delivery_polygons && cloneData.delivery_polygons.length > 0) {
            // Filter out any invalid polygons
            const validPolygons = cloneData.delivery_polygons.filter(p => p && p.length >= 3);
            if (validPolygons.length > 0) {
              setPolygons(validPolygons);
              setActivePolygonIndex(0);
              setHouseCount(cloneData.deliveries || null);
              if (cloneData.deliveries) setDataSource('cloned');
              
              // Restore any exclusion (no-delivery) polygons the previous
              // job carried — same shape as delivery_polygons.
              if (Array.isArray(cloneData.exclusion_polygons)) {
                const validExcl = cloneData.exclusion_polygons.filter(p => p && p.length >= 3);
                setExclusionPolygons(validExcl);
              }
              
              // Center map on first polygon
              const firstPolygon = validPolygons[0];
              const lats = firstPolygon.map(p => p[0]);
              const lngs = firstPolygon.map(p => p[1]);
              const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
              const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
              setMapCenter([centerLat, centerLng]);
            }
          }
          // Fall back to single boundary (legacy support)
          else if (cloneData.delivery_boundary && cloneData.delivery_boundary.length >= 3) {
            setPolygons([cloneData.delivery_boundary]);
            setActivePolygonIndex(0);
            setHouseCount(cloneData.deliveries || null);
            if (cloneData.deliveries) setDataSource('cloned');
            
            // Center map on the boundary
            const lats = cloneData.delivery_boundary.map(p => p[0]);
            const lngs = cloneData.delivery_boundary.map(p => p[1]);
            const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
            const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
            setMapCenter([centerLat, centerLng]);
          }
          
          // Set circle center if available (for Cars, Hands delivery types)
          if (cloneData.circle_center && cloneData.circle_center.length === 2) {
            setCircleCenter(cloneData.circle_center);
            setCircleRadius(cloneData.circle_radius_miles || 1);
            setMapCenter(cloneData.circle_center);
          }
          
          // Set grid sections if available
          if (cloneData.grid_sections && cloneData.grid_sections.length > 0) {
            setGridSections(cloneData.grid_sections);
          }
          
          // Set uploaded addresses for address-specific/canvassing
          if (cloneData.specific_addresses && cloneData.specific_addresses.length > 0) {
            setUploadedAddresses(cloneData.specific_addresses);
          }
          
          // Try to load cached house locations from the original job
          if (cloneData.id) {
            axios.get(`${API}/jobs/${cloneData.id}/house-locations`, getAuthHeader())
              .then(houseResponse => {
                if (houseResponse.data?.houses?.length > 0) {
                  const houses = houseResponse.data.houses.map(h => ({
                    id: h.id || '',
                    lat: h.lat,
                    lon: h.lon || h.lng,
                    address: h.address || '',
                    street: h.street || '',
                    source: h.source || 'cloned'
                  }));
                  setCountedHouseLocations(houses);
                  console.log(`Loaded ${houses.length} house locations from cloned job`);
                }
              })
              .catch(e => {
                console.warn('Could not load house locations from cloned job:', e.message);
              });
          }
          
          // Clean up sessionStorage
          sessionStorage.removeItem('cloneJobData');
          
          // Remove clone param from URL
          window.history.replaceState({}, '', '/admin/create-job');
          
          toast.success('Job cloned! Review and update the details before creating.');
    } catch (error) {
      console.error('Error in clone data processing:', error);
      sessionStorage.removeItem('cloneJobData');
      window.history.replaceState({}, '', '/admin/create-job');
    }
  }, []);

  // Check for client request pre-fill data on mount
  useEffect(() => {
    try {
      // Zoom to a specific county center if Parcel Data page sent us here
      const zoomToStr = sessionStorage.getItem('parcel_zoom_to');
      if (zoomToStr) {
        try {
          const z = JSON.parse(zoomToStr);
          if (z?.center?.lat != null && z?.center?.lng != null) {
            setMapCenter([z.center.lat, z.center.lng]);
            setMapCenterTrigger(prev => prev + 1);
            if (z.county_name) {
              toast.info(`Map centered on ${z.county_name}. Draw a zone and click Count Houses.`);
            }
          }
        } catch (zErr) {
          console.warn('parcel_zoom_to parse failed', zErr);
        }
        sessionStorage.removeItem('parcel_zoom_to');
      }

      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('from_request') !== 'true') return;

      const requestDataStr = sessionStorage.getItem('clientRequestData');
      if (!requestDataStr) {
        window.history.replaceState({}, '', '/admin/create-job');
        return;
      }

      const reqData = JSON.parse(requestDataStr);

      // Store the request_id so we can mark it fulfilled after job creation
      window.__clientRequestId = reqData.request_id || null;

      setNewJob(prev => ({
        ...prev,
        title: reqData.title || prev.title,
        client: reqData.client || prev.client,
        client_contact_name: reqData.client_contact_name || prev.client_contact_name,
        client_email: reqData.client_email || prev.client_email,
        description: reqData.description || prev.description,
        deliveries: reqData.deliveries || prev.deliveries,
      }));

      // Set delivery boundary on the map if provided
      if (reqData.delivery_boundary && reqData.delivery_boundary.length >= 3) {
        setPolygons([reqData.delivery_boundary]);
        setActivePolygonIndex(0);

        const lats = reqData.delivery_boundary.map(p => p[0]);
        const lngs = reqData.delivery_boundary.map(p => p[1]);
        const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
        const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
        setMapCenter([centerLat, centerLng]);
      }

      sessionStorage.removeItem('clientRequestData');
      window.history.replaceState({}, '', '/admin/create-job');

      toast.success('Client request loaded! Draw/adjust the zone, count houses, and create the job.');
    } catch (error) {
      console.error('Error processing client request data:', error);
      sessionStorage.removeItem('clientRequestData');
      window.history.replaceState({}, '', '/admin/create-job');
    }
  }, []);

  // Save draft function
  const handleSaveDraft = async () => {
    const validPolygons = getAllBoundaryPoints();
    if (validPolygons.length === 0 && !circleCenter) {
      toast.error('Please draw a delivery zone before saving');
      return;
    }

    setSavingDraft(true);
    try {
      const draftData = {
        name: newJob.title || `Draft - ${new Date().toLocaleDateString()}`,
        job_data: newJob,
        boundary_points: boundaryPoints, // Current active polygon for backwards compatibility
        polygons: polygons, // All polygons
        circle_center: circleCenter,
        circle_radius: circleRadius,
        house_count: useManualCount ? parseInt(manualHouseCount) : houseCount,
        grid_sections: gridSections,
        delivery_type: newJob.delivery_type,
        uploaded_addresses: uploadedAddresses,
        canvassing_turf_info: canvassingTurfInfo
      };

      const response = await axios.post(`${API}/admin/job-drafts`, draftData, getAuthHeader());
      setSavedDrafts(prev => [...prev, response.data]);
      toast.success('Draft saved successfully!');
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error('Failed to save draft');
    } finally {
      setSavingDraft(false);
    }
  };

  // Load draft function
  const handleLoadDraft = (draft) => {
    setNewJob(draft.job_data || {});
    
    // Load polygons - try new format first, then fall back to legacy
    if (draft.polygons && draft.polygons.length > 0) {
      const validPolygons = draft.polygons.filter(p => p && p.length >= 3);
      if (validPolygons.length > 0) {
        setPolygons(validPolygons);
        setActivePolygonIndex(0);
      }
    } else if (draft.boundary_points && draft.boundary_points.length >= 3) {
      // Legacy format - single polygon in boundary_points
      setPolygons([draft.boundary_points]);
      setActivePolygonIndex(0);
    }
    
    setCircleCenter(draft.circle_center);
    setCircleRadius(draft.circle_radius || DEFAULT_RADIUS_MILES);
    setHouseCount(draft.house_count);
    setGridSections(draft.grid_sections || []);
    setUploadedAddresses(draft.uploaded_addresses || []);
    setCanvassingTurfInfo(draft.canvassing_turf_info);
    
    // Set map center based on loaded data
    if (draft.circle_center && draft.circle_center.length === 2) {
      setMapCenter(draft.circle_center);
    } else if (draft.polygons && draft.polygons[0] && draft.polygons[0].length >= 3) {
      const lats = draft.polygons[0].map(p => p[0]);
      const lngs = draft.polygons[0].map(p => p[1]);
      setMapCenter([(Math.min(...lats) + Math.max(...lats)) / 2, (Math.min(...lngs) + Math.max(...lngs)) / 2]);
    } else if (draft.boundary_points && draft.boundary_points.length >= 3) {
      const lats = draft.boundary_points.map(p => p[0]);
      const lngs = draft.boundary_points.map(p => p[1]);
      setMapCenter([(Math.min(...lats) + Math.max(...lats)) / 2, (Math.min(...lngs) + Math.max(...lngs)) / 2]);
    }
    
    if (draft.house_count) {
      setManualHouseCount(draft.house_count.toString());
      setUseManualCount(true);
      setCountConfidence('manual');
    }
    
    setShowDraftsModal(false);
    toast.success('Draft loaded successfully!');
  };

  // Delete draft function
  const handleDeleteDraft = async (draftId) => {
    try {
      await axios.delete(`${API}/admin/job-drafts/${draftId}`, getAuthHeader());
      setSavedDrafts(prev => prev.filter(d => d.id !== draftId));
      toast.success('Draft deleted');
    } catch (error) {
      console.error('Error deleting draft:', error);
      toast.error('Failed to delete draft');
    }
  };

  // Apply manual house count
  const handleApplyManualCount = async () => {
    const count = parseInt(manualHouseCount);
    if (isNaN(count) || count <= 0) {
      toast.error('Please enter a valid house count');
      return;
    }

    setHouseCount(count);
    setUseManualCount(true);
    setCountConfidence('manual');
    setDataSource('manual');
    setNewJob(prev => ({ ...prev, deliveries: count }));
    
    // Recalculate grids with manual count - use all valid polygons
    const validPolygons = getAllBoundaryPoints();
    const maxPerGrid = calculateMaxPerGrid(newJob.pay_per_flyer);
    if (validPolygons.length > 0) {
      const combinedBoundary = validPolygons.length === 1 ? validPolygons[0] : getCombinedBoundingBox(validPolygons);
      const grids = calculateGridSections(combinedBoundary, count, [], maxPerGrid, validPolygons);
      setGridSections(grids);
    }
    
    toast.success(`Manual count of ${count} houses applied. Fetching coordinates...`);

    // Fetch actual house coordinates via OSM for the boundary
    if (validPolygons.length > 0) {
      await fetchOsmCoordinates(validPolygons);
    }
  };

  // AI image analysis for house counting (CountThings-style)
  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a PNG, JPG, or WEBP image');
      return;
    }

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }

    setAnalyzingImage(true);
    setAiAnalysisResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('delivery_type', newJob.delivery_type || 'houses');

      const response = await axios.post(
        `${API}/admin/count-houses-from-image`,
        formData,
        {
          ...getAuthHeader(),
          headers: {
            ...getAuthHeader().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        setAiAnalysisResult(response.data);
        const deliveryLabel = newJob.delivery_type === 'apartments' ? 'apartment units' : 'houses';
        toast.success(`AI detected ${response.data.house_count} ${deliveryLabel} (${response.data.confidence} confidence)`);
      } else {
        toast.error('Failed to analyze image');
      }
    } catch (error) {
      console.error('Error analyzing image:', error);
      toast.error(error.response?.data?.detail || 'Failed to analyze image');
    } finally {
      setAnalyzingImage(false);
      // Reset file input
      if (imageInputRef.current) {
        imageInputRef.current.value = '';
      }
    }
  };

  // Apply AI-detected count and fetch house coordinates
  const handleApplyAiCount = async () => {
    if (!aiAnalysisResult) return;

    const count = aiAnalysisResult.house_count;
    setHouseCount(count);
    setManualHouseCount(count.toString());
    setUseManualCount(true);
    setCountConfidence('ai_verified');
    setDataSource('ai_verified');
    setNewJob(prev => ({ ...prev, deliveries: count }));

    // Recalculate grids - use all valid polygons
    const validPolygons = getAllBoundaryPoints();
    const maxPerGrid = calculateMaxPerGrid(newJob.pay_per_flyer);
    if (validPolygons.length > 0) {
      const combinedBoundary = validPolygons.length === 1 ? validPolygons[0] : getCombinedBoundingBox(validPolygons);
      const grids = calculateGridSections(combinedBoundary, count, [], maxPerGrid, validPolygons);
      setGridSections(grids);
    }

    toast.success(`AI count of ${count} houses applied. Fetching coordinates...`);
    setAiAnalysisResult(null);

    // Fetch actual house coordinates via OSM for the same boundary
    if (validPolygons.length > 0) {
      await fetchOsmCoordinates(validPolygons);
    }
  };

  // Helper: fetch house coordinates from OSM as fallback
  const fetchOsmCoordinates = async (validPolygons) => {
    const houseObjects = [];
    const excludeTypes = new Set([
      'commercial', 'industrial', 'retail', 'warehouse', 
      'office', 'church', 'school', 'hospital', 'public',
      'civic', 'government', 'university', 'college',
      'supermarket', 'parking', 'garage', 'garages',
      'barn', 'farm_auxiliary', 'greenhouse', 'shed',
      'roof', 'bridge', 'construction', 'ruins'
    ]);
    for (const polygon of validPolygons) {
      const lats = polygon.map(p => p[0]);
      const lngs = polygon.map(p => p[1]);
      const minLat = Math.min(...lats), maxLat = Math.max(...lats);
      const minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
      
      try {
        const query = `[out:json][timeout:90];(way["building"](${minLat},${minLng},${maxLat},${maxLng});node["addr:housenumber"](${minLat},${minLng},${maxLat},${maxLng}););out center;`;
        const osmResponse = await fetch('https://overpass-api.de/api/interpreter', {
          method: 'POST', body: query, headers: { 'Content-Type': 'text/plain' }
        });
        if (osmResponse.ok) {
          const osmData = await osmResponse.json();
          (osmData?.elements || [])
            .filter(el => {
              const c = el.center || { lat: el.lat, lon: el.lon };
              if (!c?.lat || !c?.lon) return false;
              if (!pointInPolygon([c.lat, c.lon], polygon)) return false;
              if (el.type === 'way' && el.tags) {
                if (excludeTypes.has(el.tags.building)) return false;
                if (el.tags.amenity || el.tags.shop || el.tags.office) return false;
              }
              return true;
            })
            .forEach(el => {
              const c = el.center || { lat: el.lat, lon: el.lon };
              houseObjects.push({
                id: String(el.id || ''), lat: c.lat, lon: c.lon,
                address: el.tags?.['addr:housenumber'] || '',
                street: el.tags?.['addr:street'] || '', source: 'osm'
              });
            });
        }
      } catch (e) { console.warn('OSM fallback failed:', e.message); }
    }
    if (houseObjects.length > 0) {
      const uniqueMap = new Map();
      houseObjects.forEach(h => { uniqueMap.set(`${h.lat.toFixed(6)},${h.lon.toFixed(6)}`, h); });
      setCountedHouseLocations(Array.from(uniqueMap.values()));
      toast.info(`Found ${uniqueMap.size} coordinates from OSM`);
    } else {
      toast.warning('Could not fetch house coordinates. Houses will be searched when job is created.');
    }
  };

  // Auto-capture satellite image and analyze with AI
  const handleAutoCaptureSatellite = async () => {
    if (boundaryPoints.length < 3) {
      toast.error('Draw at least 3 points to capture satellite image');
      return;
    }

    setCapturingSatellite(true);
    setSatelliteImage(null);
    setAiAnalysisResult(null);

    try {
      const response = await axios.post(
        `${API}/admin/capture-satellite-image`,
        {
          boundary: boundaryPoints,
          delivery_type: newJob.delivery_type || 'houses',
          zoom: 18
        },
        getAuthHeader()
      );

      if (response.data.success) {
        // Store the captured image
        if (response.data.image_base64) {
          setSatelliteImage(`data:image/png;base64,${response.data.image_base64}`);
        }

        // If AI analysis was performed
        if (response.data.ai_analysis) {
          setAiAnalysisResult({
            house_count: response.data.house_count,
            confidence: response.data.confidence,
            apartment_buildings: response.data.apartment_buildings,
            notes: response.data.notes
          });
          
          const deliveryLabel = newJob.delivery_type === 'apartments' ? 'apartment units' : 'houses';
          toast.success(
            `AI detected ${response.data.house_count} ${deliveryLabel} from satellite image (${response.data.confidence} confidence)`
          );
        } else if (response.data.error) {
          toast.warning(`Satellite captured but: ${response.data.error}`);
        } else {
          toast.info('Satellite image captured. AI analysis not available.');
        }
      } else {
        toast.error('Failed to capture satellite image');
      }
    } catch (error) {
      console.error('Error capturing satellite:', error);
      toast.error(error.response?.data?.detail || 'Failed to capture satellite image');
    } finally {
      setCapturingSatellite(false);
    }
  };

  // Search for houses using Google Maps API
  const handleGoogleApiSearch = async () => {
    if (boundaryPoints.length < 3) {
      toast.error('Please draw a delivery zone first (at least 3 points)');
      return;
    }

    setSearchingGoogleApi(true);
    setGoogleApiResult(null);

    try {
      // boundaryPoints can be either [{lat, lng}] objects or [[lat, lng]] arrays
      const formattedBoundary = boundaryPoints.map(p => {
        if (Array.isArray(p)) {
          return p; // Already [lat, lng] format
        }
        return [p.lat, p.lng]; // Convert {lat, lng} to [lat, lng]
      });
      
      const response = await axios.post(
        `${API}/admin/find-houses-google`,
        {
          boundary: formattedBoundary,
          max_requests: 25
        },
        getAuthHeader()
      );

      const { houses, count, found_count, estimation_method, api_usage } = response.data;
      
      if (count > 0) {
        setGoogleApiResult({
          house_count: count,
          found_count: found_count || houses.length,
          estimation_method: estimation_method || 'count',
          houses: houses,
          api_usage: api_usage
        });
        
        // Show informative message based on estimation method
        if (found_count && found_count !== count) {
          toast.success(`Found ${found_count} addresses, estimated ${count} total houses`);
        } else {
          toast.success(`Found ${count} locations using Google Maps API`);
        }
      } else {
        toast.warning('Google API found no houses in this area. Try manual count or AI image analysis.');
        setGoogleApiResult({ house_count: 0, houses: [], api_usage: api_usage });
      }
    } catch (error) {
      console.error('Google API search error:', error);
      if (error.response?.status === 429) {
        toast.error('Google Maps API monthly limit reached. Please use manual count or AI image analysis.');
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.detail || 'Google Maps API is not configured');
      } else if (error.response?.status === 403) {
        toast.error(error.response?.data?.detail || 'Admin access required. Please log out and log back in via Admin Login.');
      } else {
        toast.error('Failed to search with Google API. Please try again.');
      }
    } finally {
      setSearchingGoogleApi(false);
    }
  };

  // Apply Google API count - also fetch actual house positions from parcel DB + OSM
  const handleApplyGoogleCount = async () => {
    if (!googleApiResult || googleApiResult.house_count === 0) return;

    const count = googleApiResult.house_count;
    setHouseCount(count);
    setManualHouseCount(count.toString());
    setUseManualCount(true);
    setCountConfidence('google_verified');
    setDataSource('google_verified');
    setNewJob(prev => ({ ...prev, deliveries: count }));

    // Recalculate grids - use all valid polygons
    const validPolygons = getAllBoundaryPoints();
    const maxPerGrid = calculateMaxPerGrid(newJob.pay_per_flyer);
    if (validPolygons.length > 0) {
      const combinedBoundary = validPolygons.length === 1 ? validPolygons[0] : getCombinedBoundingBox(validPolygons);
      const grids = calculateGridSections(combinedBoundary, count, [], maxPerGrid, validPolygons);
      setGridSections(grids);
    }

    // IMMEDIATELY set green dots from Google addresses + any existing counted locations
    const googleHouses = googleApiResult.houses || [];
    const mergedMap = new Map();
    countedHouseLocations.forEach(h => {
      if (h.lat && h.lon) mergedMap.set(`${h.lat.toFixed(6)},${h.lon.toFixed(6)}`, h);
    });
    googleHouses.forEach(h => {
      if (h.lat && h.lon) {
        const key = `${h.lat.toFixed(6)},${h.lon.toFixed(6)}`;
        if (!mergedMap.has(key)) mergedMap.set(key, h);
      }
    });
    
    // Set dots RIGHT NOW so user sees green dots immediately
    const immediatePositions = Array.from(mergedMap.values());
    setCountedHouseLocations(immediatePositions);
    setGoogleApiResult(null);
    toast.success(`Google API count of ${count} houses applied (${immediatePositions.length} positions on map)`);
    
    // Then fetch MORE positions from parcel DB + OSM in background (updates dots when complete)
    if (immediatePositions.length < count * 0.5 && validPolygons.length > 0) {
      try {
        const combinedBoundary = validPolygons.length === 1 
          ? validPolygons[0].map(p => Array.isArray(p) ? p : [p.lat, p.lng])
          : getCombinedBoundingBox(validPolygons).map(p => Array.isArray(p) ? p : [p.lat, p.lng]);
        
        const response = await axios.post(
          `${API}/admin/usa-parcel-data/find-houses-in-zone`,
          { boundary: combinedBoundary, use_parcel_db: true, use_osm: true },
          getAuthHeader()
        );
        
        if (response.data?.houses?.length > 0) {
          const updatedMap = new Map(mergedMap);
          response.data.houses.forEach(h => {
            if (h.lat && h.lon) {
              const inAnyPolygon = validPolygons.some(polygon => pointInPolygon([h.lat, h.lon], polygon));
              if (inAnyPolygon) {
                const key = `${h.lat.toFixed(6)},${h.lon.toFixed(6)}`;
                if (!updatedMap.has(key)) {
                  updatedMap.set(key, {
                    id: String(h.id || ''), lat: h.lat, lon: h.lon,
                    address: h.address || '', street: h.street || '',
                    source: h.source || 'hybrid'
                  });
                }
              }
            }
          });
          const allPositions = Array.from(updatedMap.values());
          setCountedHouseLocations(allPositions);
          toast.success(`Updated: ${allPositions.length} house positions on map`, { duration: 3000 });
        }
      } catch (err) {
        console.warn('Background parcel fetch failed:', err.message);
      }
    }
  };

  // Enhanced house search using local parcels + OSM + Google
  const handleEnhancedHouseSearch = async () => {
    const validPolygons = getAllBoundaryPoints();
    if (validPolygons.length === 0) {
      toast.error('Please draw at least one delivery zone first');
      return;
    }

    setSearchingEnhanced(true);
    setEnhancedSearchResult(null);

    try {
      const combinedBoundary = validPolygons.length === 1 
        ? validPolygons[0].map(p => Array.isArray(p) ? p : [p.lat, p.lng])
        : getCombinedBoundingBox(validPolygons).map(p => Array.isArray(p) ? p : [p.lat, p.lng]);

      // OSM only — completely separate from Parcel DB
      const response = await axios.post(
        `${API}/admin/usa-parcel-data/find-houses-in-zone`,
        { boundary: combinedBoundary, use_parcel_db: false, use_osm: true, compact: true },
        { ...getAuthHeader(), timeout: 120000 }
      );

      const houses = response.data?.houses || [];
      const count = houses.length;
      
      if (count > 0) {
        setEnhancedSearchResult({
          house_count: count, houses,
          sources_used: ['OpenStreetMap'],
          confidence: count >= 100 ? 'high' : count >= 50 ? 'medium' : 'low',
          local_parcels_available: false
        });
        toast.success(`Found ${count} houses from OpenStreetMap`);
      } else {
        toast.warning('OSM found no houses. Try Parcel DB (Count Houses) instead.');
        setEnhancedSearchResult({ 
          house_count: 0, houses: [], sources_used: ['OpenStreetMap'],
          confidence: 'none', local_parcels_available: false
        });
      }
    } catch (error) {
      console.error('OSM search error:', error);
      toast.error('OSM search failed. Please try again.');
    } finally {
      setSearchingEnhanced(false);
    }
  };

  // Apply enhanced search count
  const handleApplyEnhancedCount = () => {
    if (!enhancedSearchResult || enhancedSearchResult.house_count === 0) return;

    const count = enhancedSearchResult.house_count;
    setHouseCount(count);
    setManualHouseCount(count.toString());
    setUseManualCount(true);
    setCountConfidence('enhanced_verified');
    setDataSource('osm');
    setNewJob(prev => ({ ...prev, deliveries: count }));

    // Recalculate grids with ACTUAL house locations for accurate per-cell counts
    const validPolygons = getAllBoundaryPoints();
    const maxPerGrid = calculateMaxPerGrid(newJob.pay_per_flyer);
    if (validPolygons.length > 0) {
      const combinedBoundary = validPolygons.length === 1 ? validPolygons[0] : getCombinedBoundingBox(validPolygons);
      // Extract actual coordinates from enhanced search results
      const houseLocations = (enhancedSearchResult.houses || [])
        .filter(h => h.lat && h.lon)
        .map(h => [h.lat, h.lon]);
      const grids = calculateGridSections(combinedBoundary, count, houseLocations, maxPerGrid, validPolygons);
      setGridSections(grids);
    }

    // Merge enhanced search houses with existing counted locations
    const mergedMap = new Map();
    countedHouseLocations.forEach(h => {
      if (h.lat && h.lon) mergedMap.set(`${h.lat.toFixed(6)},${h.lon.toFixed(6)}`, h);
    });
    (enhancedSearchResult.houses || []).forEach(h => {
      if (h.lat && h.lon) {
        const key = `${h.lat.toFixed(6)},${h.lon.toFixed(6)}`;
        if (!mergedMap.has(key)) mergedMap.set(key, h);
      }
    });
    
    const allPositions = Array.from(mergedMap.values());
    toast.success(`Enhanced search count of ${count} houses applied (${allPositions.length} positions on map)`);
    setCountedHouseLocations(allPositions);
    setEnhancedSearchResult(null);
  };

  // Search address function
  const handleAddressSearch = async (query, autoSelect = false) => {
    if (!query || query.length < 3) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      return;
    }

    setSearchingAddress(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&addressdetails=1`,
        {
          headers: {
            'Accept': 'application/json',
            'User-Agent': 'LocalFlyerDelivery/1.0'
          }
        }
      );
      const data = await response.json();
      
      if (data && data.length > 0) {
        const suggestions = data.map(item => ({
          display_name: item.display_name,
          lat: parseFloat(item.lat),
          lon: parseFloat(item.lon),
          type: item.type,
          address: item.address
        }));
        
        setAddressSuggestions(suggestions);
        
        if (autoSelect && suggestions.length > 0) {
          handleSelectAddress(suggestions[0]);
        } else {
          setShowAddressSuggestions(true);
        }
      } else {
        setAddressSuggestions([]);
        setShowAddressSuggestions(false);
        toast.error('No results found for this address');
      }
    } catch (error) {
      console.error('Error searching address:', error);
      toast.error('Failed to search address');
    } finally {
      setSearchingAddress(false);
    }
  };

  // Select address suggestion
  const handleSelectAddress = (suggestion) => {
    setMapCenter([suggestion.lat, suggestion.lon]);
    setMapCenterTrigger(prev => prev + 1);
    setAddressSearchQuery(suggestion.display_name.split(',')[0]);
    setShowAddressSuggestions(false);
    
    const shortAddress = suggestion.display_name.split(',').slice(0, 3).join(',');
    setNewJob(prev => ({ ...prev, location: shortAddress }));
    
    toast.success('Map moved to selected location');
  };

  // Click outside handler for suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (addressSearchRef.current && !addressSearchRef.current.contains(event.target)) {
        setShowAddressSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Map click handlers
  const handleMapClick = (latlng) => {
    if (drawingMode) {
      setBoundaryPoints(prev => [...prev, [latlng.lat, latlng.lng]]);
    }
  };

  // ---- Radius Draw handlers ----
  const handleRadiusModeToggle = () => {
    if (drawingMode) return; // guard against future refactors dropping `disabled`
    if (radiusDrawMode) {
      setRadiusDrawMode(false);
      setRadiusCenterPlaced(false);
      return;
    }
    setRadiusDrawMode(true);
    setRadiusCenterPlaced(false);
  };

  // Called by <DeliveryZoneMap> on the second click (finalize). The map
  // already holds the live preview internally; here we convert that final
  // (center, radius) to a polygon and push it onto the zones array.
  const handleRadiusFinalize = (centerLat, centerLng, distMeters) => {
    if (!(distMeters > 0)) return;
    const ringPoints = circleToPolygon([centerLat, centerLng], distMeters, 64);
    if (ringPoints.length < 3) {
      toast.error('Radius is too small — drag the mouse farther from the center.');
      return;
    }
    setPolygons((prev) => {
      const next = [...prev];
      const insertIdx = next.length === 1 && (next[0]?.length ?? 0) === 0 ? 0 : next.length;
      if (insertIdx === next.length) {
        next.push(ringPoints);
      } else {
        next[insertIdx] = ringPoints;
      }
      setActivePolygonIndex(insertIdx);
      return next;
    });
    const miles = metersToMilesRounded(distMeters);
    toast.success(`Radius zone added — ${miles} mi.`);
    setRadiusDrawMode(false);
    setRadiusCenterPlaced(false);
  };

  const handleRadiusCancel = () => {
    setRadiusDrawMode(false);
    setRadiusCenterPlaced(false);
  };

  const handleCircleClick = (latlng) => {
    setCircleCenter([latlng.lat, latlng.lng]);
    setNewJob(prev => ({ ...prev, location: `${latlng.lat.toFixed(4)}, ${latlng.lng.toFixed(4)}` }));
  };

  // Handle marker drag to update boundary point position
  const handleMarkerDrag = (index, newPosition) => {
    setBoundaryPoints(prev => {
      const updated = [...prev];
      updated[index] = [newPosition.lat, newPosition.lng];
      return updated;
    });
  };

  // Insert a new point at a specific index (for clicking on edges)
  const handleInsertPoint = (afterIndex, position) => {
    setBoundaryPoints(prev => {
      const updated = [...prev];
      updated.splice(afterIndex + 1, 0, [position.lat, position.lng]);
      return updated;
    });
  };

  // Calculate midpoints between consecutive boundary points (for edge click targets)
  const getMidpoints = () => {
    if (boundaryPoints.length < 2) return [];
    const midpoints = [];
    for (let i = 0; i < boundaryPoints.length; i++) {
      const current = boundaryPoints[i];
      const next = boundaryPoints[(i + 1) % boundaryPoints.length];
      // Only show closing edge midpoint if we have a complete polygon (3+ points)
      if (i === boundaryPoints.length - 1 && boundaryPoints.length < 3) continue;
      midpoints.push({
        position: [(current[0] + next[0]) / 2, (current[1] + next[1]) / 2],
        afterIndex: i
      });
    }
    return midpoints;
  };

  // Multi-polygon management functions
  const addNewPolygon = () => {
    setPolygons(prev => [...prev, []]);
    setActivePolygonIndex(polygons.length);
    setDrawingMode(true);
    toast.success(`Started Zone ${polygons.length + 1}. Click on the map to draw.`);
  };

  const deletePolygon = (index) => {
    if (polygons.length <= 1) {
      // Just clear the only polygon
      setPolygons([[]]);
      setActivePolygonIndex(0);
      toast.info('Zone cleared');
      return;
    }
    
    setPolygons(prev => prev.filter((_, i) => i !== index));
    // Adjust active index if needed
    if (activePolygonIndex >= index && activePolygonIndex > 0) {
      setActivePolygonIndex(activePolygonIndex - 1);
    }
    toast.success(`Zone ${index + 1} deleted`);
  };

  const selectPolygon = (index) => {
    setActivePolygonIndex(index);
    setDrawingMode(false);
    toast.info(`Selected Zone ${index + 1} for editing`);
  };

  const clearAllPolygons = () => {
    setPolygons([[]]);
    setActivePolygonIndex(0);
    setHouseCount(null);
    setCountConfidence(null);
    setDataSource(null);
    setGridSections([]);
    // Clear the cache when polygons are cleared
    houseCountCache.current.clear();
    toast.info('All zones cleared');
  };

  // Clear cache when polygons change significantly
  const clearHouseCountCache = () => {
    houseCountCache.current.clear();
    setHouseCount(null);
    setCountConfidence(null);
    setDataSource(null);
    toast.info('House count cache cleared - click Count Houses for fresh data');
  };

  // Get total points across all polygons
  const getTotalPoints = () => polygons.reduce((sum, p) => sum + p.length, 0);

  // Get number of valid polygons (3+ points)
  const getValidPolygonCount = () => polygons.filter(p => p.length >= 3).length;

  // Triggered from the "Import parcels for [County]" action on the
  // "no parcel data for this zone" toast. Kicks off a background Parcel
  // Fetcher job and redirects the admin to /admin/parcel-converter so
  // they can watch the progress / push to V2 when ready. The fetcher
  // start endpoint expects FormData (Form fields), so we POST a URL-
  // encoded body rather than JSON.
  const startCountyImportFromToast = async (matchInfo) => {
    if (!matchInfo?.county_id) return;
    try {
      const body = new URLSearchParams();
      body.append('county_id', matchInfo.county_id);
      body.append('residential_only', 'true');
      body.append('rows_per_csv', '25000');
      const headers = {
        ...(getAuthHeader().headers || {}),
        'Content-Type': 'application/x-www-form-urlencoded',
      };
      const res = await axios.post(
        `${API}/admin/parcel-converter/fetcher/start`,
        body,
        { headers, timeout: 20000 }
      );
      toast.success(
        `Started importing ${matchInfo.name.split(' (')[0]} (~${(matchInfo.estimated_parcels || 0).toLocaleString()} parcels). ` +
        `This runs in the background — opening the Parcel Converter so you can watch progress.`,
        { duration: 8000 }
      );
      // Brief delay so the success toast is readable before navigation
      setTimeout(() => navigate('/admin/parcel-converter'), 800);
      return res.data;
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Unknown error';
      toast.error(`Could not start parcel fetch: ${detail}`, { duration: 10000 });
    }
  };

  // Count addresses in boundary - PARCEL DATABASE ONLY (no silent OSM fallback).
  // For OSM-based counts, the user must explicitly click the "Count with OSM" button
  // in the Alternative Counting Methods panel.
  const countAddressesInBoundary = useCallback(async () => {
    const validPolygons = getAllBoundaryPoints();
    if (validPolygons.length === 0) {
      toast.error('Please draw at least one complete delivery zone (3+ points)');
      return;
    }

    setCountingHouses(true);
    setCountConfidence(null);
    setDataSource(null);
    const deliveryType = newJob.delivery_type;

    // Generate cache key for these polygons. Include exclusion polygons
    // so adding/removing a red cut-out forces a fresh count.
    const cacheKey =
      validPolygons.map(p => hashPolygon(p)).join('|') +
      '||excl=' +
      (exclusionPolygons.length > 0
        ? exclusionPolygons.map(p => hashPolygon(p)).join('|')
        : 'none');

    // Check cache first - ensures consistent results
    if (houseCountCache.current.has(cacheKey)) {
      const cached = houseCountCache.current.get(cacheKey);
      setHouseCount(cached.count);
      setCountConfidence(cached.confidence);
      setDataSource(cached.dataSource || 'parcel_db');
      setAddressBreakdown(cached.breakdown);
      setNewJob(prev => ({ ...prev, deliveries: cached.count }));
      setGridSections(cached.grids);
      setCountedHouseLocations(cached.houseLocations || []);
      toast.info(`Using cached count: ${cached.count} houses (Parcel DB)`);
      setCountingHouses(false);
      return;
    }

    // Combine all polygons into one boundary for the API call
    const combinedBoundary = validPolygons.length === 1
      ? validPolygons[0].map(p => Array.isArray(p) ? p : [p.lat, p.lng])
      : getCombinedBoundingBox(validPolygons).map(p => Array.isArray(p) ? p : [p.lat, p.lng]);

    let allHouseObjects = [];
    let parcelDbCount = 0;

    toast.info(useLegacyParcelData ? 'Searching Legacy USA Parcel Database...' : 'Searching Parcel Data Mgmt (V2)...', { duration: 3000 });

    try {
      // Endpoint + payload depend on which system the user selected
      const endpoint = useLegacyParcelData
        ? `${API}/admin/usa-parcel-data/find-houses-in-zone`
        : `${API}/admin/parcel-v2/find-houses-in-zone`;
      const payload = useLegacyParcelData
        ? { boundary: combinedBoundary, use_parcel_db: true, use_osm: false, compact: true, exclusion_polygons: exclusionPolygons }
        : { boundary: combinedBoundary, compact: true, exclusion_polygons: exclusionPolygons };

      let response;
      try {
        response = await axios.post(endpoint, payload, { ...getAuthHeader(), timeout: 180000 });
      } catch (firstErr) {
        // Retry once with extended timeout for very large zones
        console.warn('Parcel DB first attempt failed, retrying with longer timeout:', firstErr.message);
        toast.warning('Large zone - retrying with extended timeout (5 min)...', { duration: 4000 });
        response = await axios.post(endpoint, payload, { ...getAuthHeader(), timeout: 300000 });
      }

      const parcelHouses = response.data?.houses || [];
      parcelDbCount = response.data?.raw_counts?.parcel_db || 0;
      const serverExcludedCount = response.data?.excluded_count || 0;
      setExcludedHouseCount(serverExcludedCount);

      // Filter to only houses within the actual polygons (not just bounding box)
      parcelHouses.forEach(h => {
        const inAnyPolygon = validPolygons.some(polygon =>
          pointInPolygon([h.lat, h.lon], polygon)
        );
        if (inAnyPolygon) {
          allHouseObjects.push({
            id: String(h.id || ''),
            lat: h.lat,
            lon: h.lon,
            address: h.address || '',
            street: h.street || '',
            source: 'parcel_db'
          });
        }
      });

      // Deduplicate houses by location
      const uniqueHouseMap = new Map();
      allHouseObjects.forEach(h => {
        const key = `${h.lat.toFixed(6)},${h.lon.toFixed(6)}`;
        if (!uniqueHouseMap.has(key)) uniqueHouseMap.set(key, h);
      });
      const uniqueHouses = Array.from(uniqueHouseMap.values());
      const totalHouseCount = uniqueHouses.length;

      if (totalHouseCount === 0) {
        // No parcel data coverage for this zone. Do NOT silently fall back to OSM
        // or area-estimation — surface the issue so the admin can decide.
        setHouseCount(null);
        setCountedHouseLocations([]);
        setAddressBreakdown(null);
        setGridSections([]);
        setCountConfidence(null);
        setDataSource(null);

        // Surface backend diagnostic if available — tells admin WHY it's 0
        // (e.g., missing 2dsphere index, no parcels at all, etc.)
        const diag = response.data?.diagnostic;
        let detailedMsg = useLegacyParcelData
          ? 'Legacy Parcel DB has no data for this zone.'
          : 'Parcel Data Mgmt (V2) has no data for this zone. No counties uploaded that cover this area yet.';
        if (useLegacyParcelData && diag) {
          if (!diag.has_2dsphere_index) {
            detailedMsg = '⚠ 2dsphere index is MISSING on the legacy parcel database! Count Houses cannot work until you click "Auto-Heal Parcel Database" on the Admin → Legacy Parcel Data page.';
          } else if (diag.parcels_in_bbox_via_latlon > 0) {
            detailedMsg = `${diag.parcels_in_bbox_via_latlon.toLocaleString()} parcels exist in this bbox but $geoWithin returned 0. Run Auto-Heal Parcel Database to backfill the location field.`;
          } else if ((diag.total_parcels_in_db || 0) > 0) {
            detailedMsg = `No parcels at this geographic location (${(diag.total_parcels_in_db || 0).toLocaleString()} parcels exist in legacy DB, but none in this area). Try a different zone or import the county.`;
          } else {
            detailedMsg = 'Legacy parcel database is empty. Import a county first.';
          }
        } else if (!useLegacyParcelData) {
          detailedMsg += ' Upload parcel data via Admin → Parcel Data Mgmt, or check "Use Legacy Parcel Data" to fall back to the legacy system.';
        }

        // ─── One-click "Import parcels for [County, State]" enhancement ───
        // Reverse-geocode the polygon centroid → matched county_id in the
        // registry. If it's an auto-fetch county, surface an "Import" action
        // on the toast that kicks off the background ArcGIS fetch in one
        // click. Falls back to the plain error toast on any failure.
        try {
          let cLat = 0, cLng = 0;
          for (const pt of combinedBoundary) {
            cLat += Array.isArray(pt) ? pt[0] : pt.lat;
            cLng += Array.isArray(pt) ? pt[1] : pt.lng;
          }
          cLat /= combinedBoundary.length;
          cLng /= combinedBoundary.length;
          const matchRes = await axios.post(
            `${API}/admin/parcel-converter/match-county`,
            { lat: cLat, lng: cLng },
            { ...getAuthHeader(), timeout: 15000 }
          );
          const m = matchRes.data;
          if (m?.match && !m.manual_only) {
            // Case A: known auto-fetch county (e.g. collin_tx) — kick off
            // the background ArcGIS fetcher in one click.
            toast.error(detailedMsg, {
              duration: 15000,
              action: {
                label: `Import parcels for ${m.name.split(' (')[0]}`,
                onClick: () => startCountyImportFromToast(m),
              },
            });
            return;
          }
          if (m?.match && m.manual_only && m.just_registered) {
            // Case B: county was UNKNOWN before this zone — we just
            // auto-registered it in both Parcel Converter + Parcel Data
            // Mgmt. Send admin straight to the new V2 county page so
            // they can upload a shapefile.
            const shortName = m.name.split(' (')[0];
            toast.success(
              `Added ${shortName} to Parcel Data Mgmt + Parcel Converter. ` +
              `Upload a parcel shapefile / CSV to populate it.`,
              {
                duration: 15000,
                action: {
                  label: `Upload parcels for ${shortName}`,
                  onClick: () => navigate(`/admin/parcel-data-v2/county/${m.county_id}`),
                },
              }
            );
            return;
          }
          if (m?.match && m.manual_only) {
            // Case C: known manual-only county (e.g. NYC, El Paso).
            const shortName = m.name.split(' (')[0];
            toast.error(`${detailedMsg} (Note: ${shortName} requires a manual shapefile upload.)`, {
              duration: 15000,
              action: {
                label: `Open ${shortName} in Parcel Data Mgmt`,
                onClick: () => navigate(`/admin/parcel-data-v2/county/${m.county_id}`),
              },
            });
            return;
          }
        } catch (matchErr) {
          // Silent fail — fall through to the plain toast below.
          console.warn('match-county lookup failed:', matchErr?.message);
        }

        toast.error(detailedMsg, { duration: 12000 });
        return;
      }

      const confidence = 'high';
      setCountConfidence(confidence);
      setDataSource(useLegacyParcelData ? 'parcel_db' : 'parcel_v2');
      setHouseCount(totalHouseCount);
      setCountedHouseLocations(uniqueHouses);

      const sourceLabel = useLegacyParcelData ? 'Parcel DB (Legacy)' : 'Parcel Data Mgmt (V2)';
      const breakdown = {
        residential: deliveryType === 'houses' ? totalHouseCount : 0,
        apartments: deliveryType === 'apartments' ? totalHouseCount : 0,
        business: 0,
        total: totalHouseCount,
        zones: validPolygons.length,
        source: sourceLabel,
        parcel_db_count: parcelDbCount,
        osm_count: 0
      };
      setAddressBreakdown(breakdown);
      setNewJob(prev => ({ ...prev, deliveries: totalHouseCount }));

      // Calculate grid sections
      const maxPerGrid = deliveryType === 'apartments' ? MAX_APARTMENTS_PER_GRID : calculateMaxPerGrid(newJob.pay_per_flyer);
      const uniqueLocations = uniqueHouses.map(h => [h.lat, h.lon]);
      const grids = calculateGridSections(combinedBoundary, totalHouseCount, uniqueLocations, maxPerGrid, validPolygons);
      setGridSections(grids);

      // Cache the results
      houseCountCache.current.set(cacheKey, {
        count: totalHouseCount,
        confidence,
        dataSource: useLegacyParcelData ? 'parcel_db_legacy' : 'parcel_v2',
        breakdown,
        grids,
        houseLocations: uniqueHouses,
        timestamp: Date.now()
      });

      toast.success(
        `Found ${totalHouseCount.toLocaleString()} ${deliveryType === 'apartments' ? 'units' : 'houses'} from ${sourceLabel} - ${grids.length} section${grids.length > 1 ? 's' : ''}` +
        (serverExcludedCount > 0
          ? ` (${serverExcludedCount.toLocaleString()} excluded by red zones)`
          : '')
      );

    } catch (error) {
      console.error('Parcel DB count failed:', error);
      // NO silent fallback — show the actual error so the admin can act.
      setHouseCount(null);
      setCountedHouseLocations([]);
      setAddressBreakdown(null);
      setGridSections([]);
      setCountConfidence(null);
      setDataSource(null);
      const detail = error?.response?.data?.detail || error?.message || 'Unknown error';
      const which = useLegacyParcelData ? 'Legacy Parcel DB' : 'Parcel Data Mgmt (V2)';
      toast.error(
        `${which} query failed: ${detail}. ${useLegacyParcelData ? 'Try "Count with OSM" or check coverage.' : 'Upload parcel data on /admin/parcel-data-v2 first, or check "Use Legacy Parcel Data" to fall back.'}`,
        { duration: 10000 }
      );
    } finally {
      setCountingHouses(false);
    }
  }, [polygons, newJob.delivery_type, newJob.pay_per_flyer]);

  // Calculate grid sections - now accepts actualPolygons to check against actual drawn zones
  const calculateGridSections = (boundary, totalCount, locations, maxPerGrid = 500, actualPolygons = null) => {
    if (boundary.length < 3 || totalCount === 0) return [];

    const numSections = Math.ceil(totalCount / maxPerGrid);
    if (numSections <= 1) {
      return [{ boundary, houseCount: totalCount, index: 1, label: 'A1', center: calculateCenter(boundary) }];
    }

    const lats = boundary.map(p => p[0]);
    const lngs = boundary.map(p => p[1]);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);

    // Helper function to check if a point is inside the polygon
    const pointInAnyPolygon = (point, polygonsToCheck) => {
      if (!polygonsToCheck || polygonsToCheck.length === 0) {
        return pointInPolygon(point, boundary);
      }
      return polygonsToCheck.some(poly => pointInPolygon(point, poly));
    };

    const checkPolygons = actualPolygons && actualPolygons.length > 0 ? actualPolygons : null;

    // Helper: check if two line segments intersect
    const segmentsIntersect = (p1, p2, p3, p4) => {
      const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
      const d1 = cross(p3, p4, p1);
      const d2 = cross(p3, p4, p2);
      const d3 = cross(p1, p2, p3);
      const d4 = cross(p1, p2, p4);
      if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
          ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) return true;
      if (d1 === 0 && onSegment(p3, p4, p1)) return true;
      if (d2 === 0 && onSegment(p3, p4, p2)) return true;
      if (d3 === 0 && onSegment(p1, p2, p3)) return true;
      if (d4 === 0 && onSegment(p1, p2, p4)) return true;
      return false;
    };
    const onSegment = (p, q, r) => {
      return r[0] <= Math.max(p[0], q[0]) && r[0] >= Math.min(p[0], q[0]) &&
             r[1] <= Math.max(p[1], q[1]) && r[1] >= Math.min(p[1], q[1]);
    };

    // Check if a polygon edge crosses any edge of a rectangle
    const polygonIntersectsCell = (poly, cMinLat, cMaxLat, cMinLng, cMaxLng) => {
      const rectEdges = [
        [[cMinLat, cMinLng], [cMinLat, cMaxLng]],
        [[cMinLat, cMaxLng], [cMaxLat, cMaxLng]],
        [[cMaxLat, cMaxLng], [cMaxLat, cMinLng]],
        [[cMaxLat, cMinLng], [cMinLat, cMinLng]],
      ];
      for (let i = 0; i < poly.length; i++) {
        const p1 = poly[i];
        const p2 = poly[(i + 1) % poly.length];
        for (const [r1, r2] of rectEdges) {
          if (segmentsIntersect(p1, p2, r1, r2)) return true;
        }
      }
      return false;
    };

    // Calculate grid dimensions - aim for roughly square cells
    // Apply latitude correction: 1° longitude is physically shorter than 1° latitude
    const avgLat = (minLat + maxLat) / 2;
    const latCorrectionFactor = Math.cos(avgLat * Math.PI / 180);
    const physicalWidth = (maxLng - minLng) * latCorrectionFactor;
    const physicalHeight = maxLat - minLat;
    const aspectRatio = physicalWidth / physicalHeight || 1;
    let cols = Math.ceil(Math.sqrt(numSections * aspectRatio));
    let rows = Math.ceil(numSections / cols);
    
    // Ensure we have enough cells
    while (rows * cols < numSections) {
      if (cols <= rows) cols++;
      else rows++;
    }

    const latStep = (maxLat - minLat) / rows;
    const lngStep = (maxLng - minLng) / cols;

    // Create non-overlapping grid cells - include ANY cell that intersects the polygon
    const gridCells = [];
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const cellMinLat = minLat + row * latStep;
        const cellMaxLat = minLat + (row + 1) * latStep;
        const cellMinLng = minLng + col * lngStep;
        const cellMaxLng = minLng + (col + 1) * lngStep;
        
        const cellCenter = [(cellMinLat + cellMaxLat) / 2, (cellMinLng + cellMaxLng) / 2];

        // Dense sampling: 5x5 grid of 25 points inside the cell for accurate overlap detection
        const samplePoints = [];
        for (let sr = 0; sr < 5; sr++) {
          for (let sc = 0; sc < 5; sc++) {
            samplePoints.push([
              cellMinLat + (sr / 4) * (cellMaxLat - cellMinLat),
              cellMinLng + (sc / 4) * (cellMaxLng - cellMinLng)
            ]);
          }
        }
        
        const pointsInside = samplePoints.filter(pt => pointInAnyPolygon(pt, checkPolygons)).length;
        const overlapRatio = pointsInside / samplePoints.length;
        
        // Determine if cell should be included via multiple methods:
        let includeCell = overlapRatio > 0;

        // Method 2: Check if any polygon edge crosses this cell's boundary
        if (!includeCell) {
          const polysToTest = checkPolygons || [boundary];
          for (const poly of polysToTest) {
            if (polygonIntersectsCell(poly, cellMinLat, cellMaxLat, cellMinLng, cellMaxLng)) {
              includeCell = true;
              break;
            }
          }
        }

        // Method 3: Check if any actual house locations fall inside this cell
        if (!includeCell && locations && locations.length > 0) {
          const hasHouseInCell = locations.some(loc => {
            const [lat, lng] = loc;
            return lat >= cellMinLat && lat <= cellMaxLat &&
                   lng >= cellMinLng && lng <= cellMaxLng;
          });
          if (hasHouseInCell) includeCell = true;
        }

        if (includeCell) {
          const rowLabel = String.fromCharCode(65 + row);
          const colLabel = col + 1;
          const label = `${rowLabel}${colLabel}`;
          
          gridCells.push({
            row,
            col,
            label,
            boundary: [
              [cellMinLat, cellMinLng],
              [cellMinLat, cellMaxLng],
              [cellMaxLat, cellMaxLng],
              [cellMaxLat, cellMinLng],
            ],
            center: cellCenter,
            overlapRatio: Math.max(overlapRatio, 0.01) // Ensure non-zero for distribution
          });
        }
      }
    }

    if (gridCells.length === 0) {
      return [{ boundary, houseCount: totalCount, index: 1, label: 'A1', center: calculateCenter(boundary) }];
    }

    // Sort cells by row then column for consistent ordering (A1, A2, A3, B1, B2, etc.)
    gridCells.sort((a, b) => {
      if (a.row !== b.row) return a.row - b.row;
      return a.col - b.col;
    });

    // Distribute house count proportionally based on overlap ratio
    const totalOverlap = gridCells.reduce((sum, cell) => sum + cell.overlapRatio, 0);
    
    const grids = gridCells.map((cell, idx) => {
      let gridHouseCount = 0;
      
      if (locations && locations.length > 0) {
        // Count actual locations in this cell
        gridHouseCount = locations.filter(loc => {
          const [lat, lng] = loc;
          const b = cell.boundary;
          return lat >= b[0][0] && lat <= b[2][0] &&
                 lng >= b[0][1] && lng <= b[1][1] &&
                 pointInPolygon(loc, boundary);
        }).length;
      } else {
        // Distribute proportionally based on overlap
        gridHouseCount = Math.round(totalCount * (cell.overlapRatio / totalOverlap));
      }

      return {
        boundary: cell.boundary,
        houseCount: gridHouseCount,
        index: idx + 1,
        label: cell.label,
        center: cell.center
      };
    });

    // Ensure total house count is distributed correctly
    const totalAssigned = grids.reduce((sum, g) => sum + g.houseCount, 0);
    if (totalAssigned !== totalCount && grids.length > 0) {
      const diff = totalCount - totalAssigned;
      if (diff > 0) {
        // Add remaining houses to cells with highest overlap
        const sortedByOverlap = [...gridCells].sort((a, b) => b.overlapRatio - a.overlapRatio);
        for (let i = 0; i < diff; i++) {
          const targetLabel = sortedByOverlap[i % sortedByOverlap.length].label;
          const grid = grids.find(g => g.label === targetLabel);
          if (grid) grid.houseCount++;
        }
      } else {
        // Remove excess from cells with lowest overlap
        const sortedByOverlap = [...gridCells].sort((a, b) => a.overlapRatio - b.overlapRatio);
        for (let i = 0; i < Math.abs(diff); i++) {
          const targetLabel = sortedByOverlap[i % sortedByOverlap.length].label;
          const grid = grids.find(g => g.label === targetLabel);
          if (grid && grid.houseCount > 0) grid.houseCount--;
        }
      }
    }

    return grids;
  };

  // Handle file upload for address specific
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validTypes = ['.csv', '.xlsx', '.xls'];
    const fileExt = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!validTypes.includes(fileExt)) {
      toast.error('Please upload a CSV or Excel file');
      return;
    }

    setUploadingFile(true);
    setGeocodingProgress(null);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/jobs/upload-addresses`,
        formData,
        {
          ...getAuthHeader(),
          headers: {
            ...getAuthHeader().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.status === 'processing' && response.data.task_id) {
        // Background task - poll for results
        setGeocodingProgress({ total: response.data.total, geocoded: 0, failed: 0 });
        toast.info(`Geocoding ${response.data.total} addresses from file...`);
        await pollGeocodingTask(response.data.task_id);
      } else {
        setUploadedAddresses(response.data.addresses);
        setNewJob(prev => ({ ...prev, deliveries: response.data.addresses.length }));
        const geocoded = response.data.addresses.filter(a => a.lat && a.lng).length;
        toast.success(`Loaded ${response.data.total} addresses (${geocoded} geocoded)`);
        if (response.data.grid_sections && response.data.grid_sections.length > 1) {
          setGridSections(response.data.grid_sections);
          toast.info(`Auto-split into ${response.data.grid_sections.length} grid sections (500+ addresses clustered)`);
        }
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(error.response?.data?.detail || 'Failed to process file');
    } finally {
      setUploadingFile(false);
      setGeocodingProgress(null);
    }
  };

  // Poll geocoding background task until complete
  const pollGeocodingTask = async (taskId) => {
    const maxAttempts = 600; // Up to 10 minutes (polling every 1s)
    for (let i = 0; i < maxAttempts; i++) {
      try {
        const res = await axios.get(
          `${BACKEND_URL}/api/admin/jobs/geocoding-status/${taskId}`,
          getAuthHeader()
        );
        const { status, total, geocoded, failed, addresses, grid_sections } = res.data;
        setGeocodingProgress({ total, geocoded, failed });
        
        if (status === 'complete') {
          setUploadedAddresses(addresses);
          setNewJob(prev => ({ ...prev, deliveries: addresses.length }));
          const geocodedCount = addresses.filter(a => a.lat && a.lng).length;
          toast.success(`Geocoded ${geocodedCount}/${total} addresses`);
          // Auto-apply grid sections if clusters detected
          if (grid_sections && grid_sections.length > 1) {
            setGridSections(grid_sections);
            toast.info(`Auto-split into ${grid_sections.length} grid sections (500+ addresses clustered)`);
          }
          return;
        } else if (status === 'error') {
          toast.error(res.data.error || 'Geocoding failed');
          return;
        }
      } catch (err) {
        console.error('Poll error:', err);
      }
      await new Promise(r => setTimeout(r, 1000));
    }
    toast.error('Geocoding timed out. Please try with fewer addresses.');
  };

  // Handle copy/paste addresses
  const handlePasteAddresses = async () => {
    if (!pasteAddressText.trim()) {
      toast.error('Please paste some addresses first');
      return;
    }
    setParsingPastedAddresses(true);
    setGeocodingProgress(null);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/jobs/parse-raw-addresses`,
        { text: pasteAddressText },
        getAuthHeader()
      );
      
      if (response.data.status === 'processing' && response.data.task_id) {
        // Background task - poll for results
        setGeocodingProgress({ total: response.data.total, geocoded: 0, failed: 0 });
        toast.info(`Geocoding ${response.data.total} addresses in background...`);
        await pollGeocodingTask(response.data.task_id);
      } else {
        // Synchronous result (small list)
        setUploadedAddresses(response.data.addresses);
        setNewJob(prev => ({ ...prev, deliveries: response.data.addresses.length }));
        const geocoded = response.data.addresses.filter(a => a.lat && a.lng).length;
        toast.success(`Loaded ${response.data.total} addresses (${geocoded} geocoded)`);
        if (response.data.grid_sections && response.data.grid_sections.length > 1) {
          setGridSections(response.data.grid_sections);
          toast.info(`Auto-split into ${response.data.grid_sections.length} grid sections (500+ addresses clustered)`);
        }
      }
    } catch (error) {
      console.error('Error parsing pasted addresses:', error);
      toast.error(error.response?.data?.detail || 'Failed to parse addresses');
    } finally {
      setParsingPastedAddresses(false);
      setGeocodingProgress(null);
    }
  };

  // Handle PDF upload for canvassing (streaming version)
  const handleCanvassingPdfUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please upload a PDF file');
      return;
    }

    // Check file size (warn if > 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.info('Large PDF detected. Addresses will appear as they are found...');
    }

    setProcessingPdf(true);
    setCanvassingTurfInfo(null);
    setUploadedAddresses([]); // Clear previous addresses
    
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      
      // Use streaming endpoint
      const response = await fetch(`${BACKEND_URL}/api/admin/parse-canvassing-pdf-stream`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to process PDF');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let addressList = [];
      let turfInfo = null;

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        
        // Process complete SSE messages
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || ''; // Keep incomplete message in buffer
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              switch (data.type) {
                case 'init':
                  // Initial info received
                  break;
                  
                case 'turf_info':
                  turfInfo = { turf_name: data.turf_name };
                  setCanvassingTurfInfo(prev => ({ ...prev, turf_name: data.turf_name }));
                  setNewJob(prev => ({ ...prev, title: data.turf_name || prev.title }));
                  break;
                  
                case 'progress':
                  // Could show page progress if needed
                  break;
                  
                case 'address':
                  // Add address to list and update state
                  const newAddr = {
                    lat: null,
                    lng: null,
                    address: data.address,
                    type: 'address'
                  };
                  addressList.push(newAddr);
                  
                  // Update state with new address (creates visual effect)
                  setUploadedAddresses([...addressList]);
                  setNewJob(prev => ({ ...prev, deliveries: addressList.length }));
                  break;
                  
                case 'complete':
                  setCanvassingTurfInfo(data.turf_info);
                  toast.success(`Extracted ${data.total_addresses} addresses from turf packet`);
                  break;
                  
                case 'error':
                  throw new Error(data.message);
              }
            } catch (parseError) {
              console.error('Error parsing SSE data:', parseError);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error processing PDF:', error);
      toast.error(error.message || 'Failed to process PDF');
      setUploadedAddresses([]);
    } finally {
      setProcessingPdf(false);
    }
  };

  // Create job
  const handleCreateJob = async () => {
    if (!newJob.title || !newJob.client) {
      toast.error('Please fill in job title and client name');
      return;
    }

    if (!newJob.pickup_location || !newJob.pickup_date) {
      toast.error('Please fill in pickup location and pickup date');
      return;
    }

    const deliveryType = DELIVERY_TYPES.find(t => t.value === newJob.delivery_type);
    const validPolygons = getAllBoundaryPoints();

    if (deliveryType?.zoneType === 'polygon' && validPolygons.length === 0) {
      toast.error('Please draw a delivery zone on the map');
      return;
    }

    if (deliveryType?.zoneType === 'circle' && !circleCenter) {
      toast.error('Please click on the map to set a center point');
      return;
    }

    if ((deliveryType?.value === 'address_specific' || deliveryType?.value === 'canvassing') && uploadedAddresses.length === 0) {
      toast.error('Please add addresses by pasting them or uploading a file');
      return;
    }

    setCreatingJobs(true);

    try {
      const combinedBoundary = validPolygons.length === 1 ? validPolygons[0] : getCombinedBoundingBox(validPolygons);
      
      // Collect house locations based on delivery type
      let houseLocations = [];
      if (deliveryType?.value === 'address_specific' || deliveryType?.value === 'canvassing') {
        // For address-based types, convert uploaded addresses to house_locations format
        houseLocations = uploadedAddresses
          .filter(a => a.lat && a.lng)
          .map((a, idx) => ({
            id: `addr_${idx}`,
            lat: a.lat,
            lon: a.lng,
            address: a.address || a.street || '',
            street: a.street || '',
            city: a.city || '',
            source: 'uploaded'
          }));
      } else if (deliveryType?.zoneType === 'polygon' && validPolygons.length > 0) {
        // For polygon types, use already counted locations or count now
        houseLocations = countedHouseLocations.length > 0
          ? countedHouseLocations
          : (enhancedSearchResult?.houses || googleApiResult?.houses || []);

        // If no house locations found yet, auto-count before creating job
        if (houseLocations.length === 0) {
          toast.info('Counting houses before creating job...');

          // Search using OSM
          const excludeTypes = new Set([
              'commercial', 'industrial', 'retail', 'warehouse', 'office',
              'church', 'school', 'hospital', 'public', 'civic', 'government',
              'supermarket', 'parking', 'garage', 'garages', 'barn', 'shed',
              'roof', 'bridge', 'construction', 'ruins'
            ]);
            for (const polygon of validPolygons) {
              const lats = polygon.map(p => p[0]);
              const lngs = polygon.map(p => p[1]);
              const minLat = Math.min(...lats), maxLat = Math.max(...lats);
              const minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
              try {
                const query = `[out:json][timeout:90];(way["building"](${minLat},${minLng},${maxLat},${maxLng});node["addr:housenumber"](${minLat},${minLng},${maxLat},${maxLng}););out center;`;
                const osmResponse = await fetch('https://overpass-api.de/api/interpreter', {
                  method: 'POST', body: query, headers: { 'Content-Type': 'text/plain' }
                });
                if (osmResponse.ok) {
                  const osmData = await osmResponse.json();
                  (osmData?.elements || [])
                    .filter(el => {
                      const c = el.center || { lat: el.lat, lon: el.lon };
                      if (!c?.lat || !c?.lon) return false;
                      if (!pointInPolygon([c.lat, c.lon], polygon)) return false;
                      if (el.type === 'way' && el.tags) {
                        if (excludeTypes.has(el.tags.building)) return false;
                        if (el.tags.amenity || el.tags.shop || el.tags.office) return false;
                      }
                      return true;
                    })
                    .forEach(el => {
                      const c = el.center || { lat: el.lat, lon: el.lon };
                      houseLocations.push({
                        id: String(el.id || ''), lat: c.lat, lon: c.lon,
                        address: el.tags?.['addr:housenumber'] || '',
                        street: el.tags?.['addr:street'] || '', source: 'osm'
                      });
                    });
                }
              } catch (e) { console.warn('OSM auto-count failed:', e.message); }
            }
            // Deduplicate
            const uniqueMap = new Map();
            houseLocations.forEach(h => { uniqueMap.set(`${h.lat.toFixed(6)},${h.lon.toFixed(6)}`, h); });
            houseLocations = Array.from(uniqueMap.values());
            if (houseLocations.length > 0) {
              toast.success(`OSM found ${houseLocations.length} houses`);
            }

          // Update the deliveries count to match actual houses found
          if (houseLocations.length > 0) {
            newJob.deliveries = houseLocations.length;
          }
        }
      } else {
        // Circle/other types — use whatever was counted
        houseLocations = countedHouseLocations.length > 0 ? countedHouseLocations : [];
      }

      const jobData = {
        ...newJob,
        // Use actual house count if houses were found
        deliveries: houseLocations.length > 0 ? houseLocations.length : newJob.deliveries,
        delivery_boundary: deliveryType?.zoneType === 'polygon' ? combinedBoundary : [],
        delivery_polygons: deliveryType?.zoneType === 'polygon' ? validPolygons : [], // Store all polygons
        // Exclusion polygons (red no-delivery cut-outs). Persisted so the
        // delivery worker app + reporting also subtract them.
        exclusion_polygons: exclusionPolygons,
        map_center: deliveryType?.zoneType === 'circle' ? circleCenter : calculateCenter(combinedBoundary.length > 0 ? combinedBoundary : [[0,0]]),
        grid_sections: gridSections,
        circle_center: circleCenter,
        circle_radius_miles: circleRadius,
        specific_addresses: (deliveryType?.value === 'address_specific' || deliveryType?.value === 'canvassing') ? uploadedAddresses : [],
        total_pay: (houseLocations.length > 0 ? houseLocations.length : newJob.deliveries) * newJob.pay_per_flyer,
        status: 'pending_approval',
        // Always send house_locations inline - strip to {lat, lon} for large sets to keep payload small
        house_locations: houseLocations.length > 0 
          ? (houseLocations.length >= 500 
              ? houseLocations.map(h => ({ lat: h.lat, lon: h.lon, address: h.address || '', source: h.source || 'osm' }))
              : houseLocations)
          : undefined
      };

      // For address-specific with auto grid-split: create parent + child jobs
      const isAddressGridSplit = (deliveryType?.value === 'address_specific' || deliveryType?.value === 'canvassing') 
        && gridSections.length > 1 
        && uploadedAddresses.length >= 500;
      
      if (isAddressGridSplit) {
        // For address map center, use centroid of geocoded addresses
        const geocodedAddrs = uploadedAddresses.filter(a => a.lat && a.lng);
        if (geocodedAddrs.length > 0) {
          jobData.map_center = [
            geocodedAddrs.reduce((s, a) => s + a.lat, 0) / geocodedAddrs.length,
            geocodedAddrs.reduce((s, a) => s + a.lng, 0) / geocodedAddrs.length
          ];
          // Build a bounding box as delivery_boundary
          const lats = geocodedAddrs.map(a => a.lat);
          const lngs = geocodedAddrs.map(a => a.lng);
          const pad = 0.002;
          jobData.delivery_boundary = [
            [Math.min(...lats) - pad, Math.min(...lngs) - pad],
            [Math.min(...lats) - pad, Math.max(...lngs) + pad],
            [Math.max(...lats) + pad, Math.max(...lngs) + pad],
            [Math.max(...lats) + pad, Math.min(...lngs) - pad],
          ];
        }
        
        // Create parent job first
        const parentResponse = await axios.post(`${BACKEND_URL}/api/admin/jobs`, jobData, getAuthHeader());
        const parentId = parentResponse.data.id;
        toast.info(`Parent job created. Creating ${gridSections.length} child grid jobs...`);
        
        // Create child jobs for each grid section
        let successCount = 0;
        for (const grid of gridSections) {
          try {
            // Get addresses that belong to this grid cell
            const gridAddresses = grid.address_indices
              ? grid.address_indices.map(idx => uploadedAddresses[idx]).filter(Boolean)
              : uploadedAddresses.filter(a => {
                  if (!a.lat || !a.lng) return false;
                  const b = grid.boundary;
                  return a.lat >= b[0][0] && a.lat <= b[2][0] && a.lng >= b[0][1] && a.lng <= b[1][1];
                });
            
            const childHouseLocations = gridAddresses
              .filter(a => a.lat && a.lng)
              .map((a, idx) => ({ id: `addr_${idx}`, lat: a.lat, lon: a.lng, address: a.address || '', source: 'uploaded' }));
            
            const childJobData = {
              title: `${newJob.title} - ${grid.label}`,
              client: newJob.client,
              client_contact_name: newJob.client_contact_name,
              client_phone: newJob.client_phone,
              client_email: newJob.client_email,
              location: newJob.location,
              delivery_type: newJob.delivery_type,
              deliveries: grid.houseCount,
              pay_per_flyer: newJob.pay_per_flyer,
              deadline: newJob.deadline || new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0],
              flyer_type: newJob.flyer_type || 'Standard',
              pickup_location: newJob.pickup_location || newJob.location || 'TBD',
              pickup_date: newJob.pickup_date || new Date().toISOString().split('T')[0],
              description: `${newJob.description || ''}\n[Grid Section: ${grid.label}]`.trim(),
              delivery_boundary: grid.boundary,
              map_center: grid.center,
              grid_sections: [grid],
              specific_addresses: gridAddresses,
              client_payment: Math.round((grid.houseCount / uploadedAddresses.length) * (newJob.client_payment || 0)),
              total_pay: grid.houseCount * newJob.pay_per_flyer,
              status: 'pending_approval',
              parent_job_id: parentId,
              house_locations: childHouseLocations.length > 0 && childHouseLocations.length < 500 ? childHouseLocations : undefined
            };
            
            const childResp = await axios.post(`${BACKEND_URL}/api/admin/jobs`, childJobData, getAuthHeader());
            
            // Save large house locations separately
            if (childHouseLocations.length >= 500 && childResp.data?.id) {
              try {
                await axios.post(`${BACKEND_URL}/api/admin/jobs/${childResp.data.id}/save-house-locations`, { houses: childHouseLocations }, getAuthHeader());
              } catch (e) { console.warn(`Could not save house locations for grid ${grid.label}:`, e.message); }
            }
            successCount++;
          } catch (err) {
            console.error(`Failed to create child job for grid ${grid.label}:`, err);
          }
        }
        
        // Mark parent as split
        if (successCount > 0) {
          try { await axios.post(`${BACKEND_URL}/api/admin/jobs/${parentId}/mark-as-split`, {}, getAuthHeader()); } catch (e) { console.warn('Could not mark parent as split:', e); }
          toast.success(`Created ${successCount} grid child jobs from ${uploadedAddresses.length} addresses!`);
        }
      } else {
        // Standard single job creation (no split)
        // For address types, compute map center from geocoded addresses
        if ((deliveryType?.value === 'address_specific' || deliveryType?.value === 'canvassing') && uploadedAddresses.length > 0) {
          const geocodedAddrs = uploadedAddresses.filter(a => a.lat && a.lng);
          if (geocodedAddrs.length > 0) {
            jobData.map_center = [
              geocodedAddrs.reduce((s, a) => s + a.lat, 0) / geocodedAddrs.length,
              geocodedAddrs.reduce((s, a) => s + a.lng, 0) / geocodedAddrs.length
            ];
          }
          // Convert addresses to house_locations format
          jobData.house_locations = uploadedAddresses
            .filter(a => a.lat && a.lng)
            .map((a, idx) => ({ id: `addr_${idx}`, lat: a.lat, lon: a.lng, address: a.address || '', source: 'uploaded' }));
        }
        
        const jobResponse = await axios.post(`${BACKEND_URL}/api/admin/jobs`, jobData, getAuthHeader());
        console.log('Job created successfully:', jobResponse.data);
        toast.success(`Job created with ${houseLocations.length > 0 ? houseLocations.length : newJob.deliveries} houses!`);
      }
      
      // Mark the client request as fulfilled if this job was created from a request
      if (window.__clientRequestId) {
        try {
          await axios.put(`${BACKEND_URL}/api/admin/job-requests/${window.__clientRequestId}/status?status=fulfilled`, {}, getAuthHeader());
          window.__clientRequestId = null;
        } catch (e) {
          console.warn('Could not update client request status:', e);
        }
      }
      
      // Navigate back to jobs list
      setTimeout(() => {
        navigate('/admin/jobs');
      }, 500);
    } catch (error) {
      console.error('Error creating job:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to create job');
    } finally {
      setCreatingJobs(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setNewJob({
      title: '',
      client: '',
      client_contact_name: '',
      client_phone: '',
      client_email: '',
      location: '',
      deliveries: 0,
      pay_per_flyer: 0.25,
      bonus_pay: 0,
      deadline: '',
      flyer_type: 'Standard',
      description: '',
      delivery_type: 'houses',
      client_payment: 0
    });
    setBoundaryPoints([]);
    setCircleCenter(null);
    setCircleRadius(DEFAULT_RADIUS_MILES);
    setHouseCount(null);
    setAddressBreakdown(null);
    setGridSections([]);
    setCountConfidence(null);
    setDataSource(null);
    setCountedHouseLocations([]);
    setUploadedAddresses([]);
    setPasteAddressText('');
    setDrawingMode(false);
    setCanvassingTurfInfo(null);
  };

  const currentDeliveryType = DELIVERY_TYPES.find(t => t.value === newJob.delivery_type);
  const needsGrid = currentDeliveryType?.needsGrid;

  return (
    <div className="p-6" data-testid="create-job-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="outline"
          onClick={() => navigate('/admin/jobs')}
          className="flex items-center gap-2"
        >
          <ChevronLeft size={18} />
          Back to Jobs
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create New Job</h1>
          <p className="text-sm text-gray-500">Define delivery zone and job details</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {/* Full Width Row - Job Details */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Briefcase size={20} className="text-purple-600" />
            Job Details
          </h2>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label className="text-sm font-medium">Job Title *</Label>
                <Input
                  placeholder="e.g., Maple Grove Neighborhood"
                  value={newJob.title}
                  onChange={(e) => setNewJob({ ...newJob, title: e.target.value })}
                  data-testid="job-title-input"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Client Name *</Label>
                <Input
                  placeholder="e.g., Joe's Pizza"
                  value={newJob.client}
                  onChange={(e) => setNewJob({ ...newJob, client: e.target.value })}
                  data-testid="job-client-input"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Contact Name</Label>
                <Input
                  placeholder="Contact person"
                  value={newJob.client_contact_name}
                  onChange={(e) => setNewJob({ ...newJob, client_contact_name: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Contact Phone</Label>
                <Input
                  placeholder="Phone number"
                  value={newJob.client_phone}
                  onChange={(e) => setNewJob({ ...newJob, client_phone: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label className="text-sm font-medium">Client Email</Label>
                <Input
                  type="email"
                  placeholder="client@example.com"
                  value={newJob.client_email}
                  onChange={(e) => setNewJob({ ...newJob, client_email: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Pay per Flyer ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newJob.pay_per_flyer}
                  onChange={(e) => setNewJob({ ...newJob, pay_per_flyer: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Bonus Pay ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newJob.bonus_pay}
                  onChange={(e) => setNewJob({ ...newJob, bonus_pay: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Deadline</Label>
                <Input
                  type="date"
                  value={newJob.deadline}
                  onChange={(e) => setNewJob({ ...newJob, deadline: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium">Delivery Type</Label>
              <div className="grid grid-cols-6 gap-2 mt-2">
                {DELIVERY_TYPES.map(type => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => {
                      setNewJob({ ...newJob, delivery_type: type.value });
                      setBoundaryPoints([]);
                      setCircleCenter(null);
                      setHouseCount(null);
                      setGridSections([]);
                      setUploadedAddresses([]);
                      setCanvassingTurfInfo(null);
                    }}
                    className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-1 ${
                      newJob.delivery_type === type.value
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid={`delivery-type-${type.value}`}
                  >
                    <span className="text-xl">{type.icon}</span>
                    <span className="text-xs font-medium">{type.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Pickup Materials Section */}
            <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg space-y-4 mt-4">
              <h4 className="font-medium text-orange-800 flex items-center gap-2">
                <Package size={18} />
                Pickup Materials Info (Required)
              </h4>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label className="text-sm font-medium">Pickup Location *</Label>
                  <Input
                    placeholder="Address where flyers can be picked up"
                    value={newJob.pickup_location}
                    onChange={(e) => setNewJob({ ...newJob, pickup_location: e.target.value })}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium">Pickup Date *</Label>
                    <Input
                      type="date"
                      value={newJob.pickup_date}
                      onChange={(e) => setNewJob({ ...newJob, pickup_date: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Pickup Time</Label>
                    <Input
                      type="time"
                      value={newJob.pickup_time}
                      onChange={(e) => setNewJob({ ...newJob, pickup_time: e.target.value })}
                      placeholder="e.g., 9:00 AM"
                    />
                  </div>
                </div>
              </div>
            </div>

            {isFullAdmin && (
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <Label className="text-sm font-medium text-blue-600">Client Payment ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newJob.client_payment}
                    onChange={(e) => setNewJob({ ...newJob, client_payment: parseFloat(e.target.value) || 0 })}
                  />
                  <p className="text-xs text-blue-600 mt-1">Total amount client pays</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-green-600">Client Cost Per House ($)</Label>
                  <Input
                    type="number"
                    step="0.001"
                    min="0"
                    placeholder="0.00"
                    value={newJob.client_cost_per_delivery || ''}
                    onChange={(e) => setNewJob({ ...newJob, client_cost_per_delivery: parseFloat(e.target.value) || 0 })}
                    data-testid="client-cost-per-delivery-input"
                  />
                  <p className="text-xs text-green-600 mt-1">For client cost analysis (shown on approval link)</p>
                </div>
              </div>
            )}

            <div className="mt-4">
              <Label className="text-sm font-medium">Description</Label>
              <textarea
                className="w-full px-3 py-2 border rounded-lg text-sm"
                rows={3}
                placeholder="Additional notes..."
                value={newJob.description}
                onChange={(e) => setNewJob({ ...newJob, description: e.target.value })}
              />
            </div>

            {/* Summary */}
            <div className="p-4 bg-gray-50 rounded-lg mt-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Total Deliveries:</span>
                  <span className="ml-2 font-bold">{newJob.deliveries}</span>
                </div>
                <div>
                  <span className="text-gray-500">Total Pay:</span>
                  <span className="ml-2 font-bold text-green-600">
                    ${(newJob.deliveries * newJob.pay_per_flyer + newJob.bonus_pay).toFixed(2)}
                  </span>
                </div>
                {gridSections.length > 1 && (
                  <div className="col-span-2">
                    <span className="text-gray-500">Job Sections:</span>
                    <span className="ml-2 font-bold text-purple-600">{gridSections.length}</span>
                    <span className="text-gray-400 ml-1">(~${Math.round((newJob.deliveries / gridSections.length) * newJob.pay_per_flyer)}/job)</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Full Width Row - Delivery Zone Map */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <MapPin size={20} className="text-purple-600" />
            Delivery Zone Map
          </h2>

          {/* Address Search */}
          <div className="mb-4 relative" ref={addressSearchRef}>
            <Label className="text-sm font-medium mb-1 block">Search Location</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Enter address to center map..."
                  value={addressSearchQuery}
                  onChange={(e) => setAddressSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && addressSearchQuery.length >= 3) {
                      e.preventDefault();
                      handleAddressSearch(addressSearchQuery, true);
                    }
                  }}
                  className="pl-10"
                  data-testid="address-search-input"
                />
              </div>
              <Button
                type="button"
                onClick={() => {
                  if (addressSearchQuery.length >= 3) {
                    handleAddressSearch(addressSearchQuery, true);
                  } else {
                    toast.error('Please enter at least 3 characters');
                  }
                }}
                disabled={searchingAddress || addressSearchQuery.length < 3}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="address-search-btn"
              >
                {searchingAddress ? <Loader2 size={18} className="animate-spin" /> : <><Search size={18} className="mr-1" /> Search</>}
              </Button>
            </div>

            {showAddressSuggestions && addressSuggestions.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {addressSuggestions.map((suggestion, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => handleSelectAddress(suggestion)}
                    className="w-full px-4 py-3 text-left hover:bg-purple-50 border-b last:border-b-0"
                  >
                    <div className="flex items-start gap-2">
                      <MapPin size={16} className="text-purple-500 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium">{suggestion.display_name.split(',')[0]}</p>
                        <p className="text-xs text-gray-500">{suggestion.display_name.split(',').slice(1, 4).join(',')}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Address Specific - File Upload & Copy/Paste */}
          {newJob.delivery_type === 'address_specific' && (
            <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <Label className="text-sm font-medium text-blue-800 flex items-center gap-2 mb-3">
                <FileSpreadsheet size={18} />
                Add Addresses
              </Label>

              {/* Copy/Paste Section */}
              <div className="mb-3">
                <p className="text-xs text-blue-600 mb-2">
                  Paste addresses below, one per line (e.g. "123 Main St, City, State ZIP")
                </p>
                <textarea
                  data-testid="paste-addresses-textarea"
                  className="w-full h-32 p-3 text-sm border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 resize-y bg-white"
                  placeholder={"123 Main St, Springfield, IL 62704\n456 Oak Ave, Springfield, IL 62701\n789 Pine Rd, Springfield, IL 62702"}
                  value={pasteAddressText}
                  onChange={(e) => setPasteAddressText(e.target.value)}
                  disabled={parsingPastedAddresses}
                />
                <div className="flex items-center gap-3 mt-2">
                  <Button
                    type="button"
                    data-testid="process-pasted-addresses-btn"
                    onClick={handlePasteAddresses}
                    disabled={parsingPastedAddresses || !pasteAddressText.trim()}
                    className="bg-blue-600 hover:bg-blue-700 text-white text-sm"
                    size="sm"
                  >
                    {parsingPastedAddresses ? (
                      <><Loader2 size={14} className="animate-spin mr-1" /> Geocoding...</>
                    ) : (
                      <><MapPin size={14} className="mr-1" /> Process Addresses</>
                    )}
                  </Button>
                  {pasteAddressText.trim() && !parsingPastedAddresses && (
                    <span className="text-xs text-blue-500">
                      {pasteAddressText.trim().split('\n').filter(l => l.trim()).length} lines detected
                    </span>
                  )}
                </div>

                {/* Geocoding Progress Bar */}
                {geocodingProgress && parsingPastedAddresses && (
                  <div className="mt-3" data-testid="geocoding-progress">
                    <div className="flex items-center justify-between text-xs text-blue-700 mb-1">
                      <span>Geocoding: {geocodingProgress.geocoded + geocodingProgress.failed} / {geocodingProgress.total}</span>
                      <span>{Math.round(((geocodingProgress.geocoded + geocodingProgress.failed) / geocodingProgress.total) * 100)}%</span>
                    </div>
                    <div className="w-full bg-blue-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${((geocodingProgress.geocoded + geocodingProgress.failed) / geocodingProgress.total) * 100}%` }}
                      />
                    </div>
                    {geocodingProgress.failed > 0 && (
                      <span className="text-xs text-amber-600 mt-1">{geocodingProgress.failed} failed to geocode</span>
                    )}
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className="flex items-center gap-2 my-3">
                <div className="flex-1 border-t border-blue-200" />
                <span className="text-xs text-blue-400 font-medium">OR</span>
                <div className="flex-1 border-t border-blue-200" />
              </div>

              {/* File Upload Section */}
              <p className="text-xs text-blue-600 mb-2">
                Upload a CSV or Excel file with addresses (columns: address, city, state, zip)
              </p>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700 text-sm">
                  <Upload size={16} />
                  <span className="font-medium">Choose File</span>
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileUpload}
                    className="hidden"
                    disabled={uploadingFile}
                    data-testid="address-file-upload-input"
                  />
                </label>
                {uploadingFile && <Loader2 size={20} className="animate-spin text-blue-600" />}
              </div>

              {/* File Upload Geocoding Progress */}
              {geocodingProgress && uploadingFile && (
                <div className="mt-3" data-testid="file-geocoding-progress">
                  <div className="flex items-center justify-between text-xs text-blue-700 mb-1">
                    <span>Geocoding: {geocodingProgress.geocoded + geocodingProgress.failed} / {geocodingProgress.total}</span>
                    <span>{Math.round(((geocodingProgress.geocoded + geocodingProgress.failed) / geocodingProgress.total) * 100)}%</span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-2">
                    <div className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${((geocodingProgress.geocoded + geocodingProgress.failed) / geocodingProgress.total) * 100}%` }} />
                  </div>
                </div>
              )}

              {/* Loaded addresses indicator */}
              {uploadedAddresses.length > 0 && (
                <div className="mt-3 space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className="bg-green-100 text-green-700">
                      {uploadedAddresses.length} addresses loaded
                    </Badge>
                    <span className="text-xs text-green-600">
                      ({uploadedAddresses.filter(a => a.lat && a.lng).length} geocoded)
                    </span>
                    {gridSections.length > 1 && (
                      <Badge className="bg-purple-100 text-purple-700">
                        Auto-split: {gridSections.length} grid sections
                      </Badge>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-xs text-red-500 hover:text-red-700 h-6 px-2"
                      onClick={() => { setUploadedAddresses([]); setPasteAddressText(''); setGridSections([]); setNewJob(prev => ({ ...prev, deliveries: 0 })); }}
                      data-testid="clear-addresses-btn"
                    >
                      <X size={12} className="mr-1" /> Clear
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Canvassing PDF Upload */}
          {newJob.delivery_type === 'canvassing' && (
            <div className="mb-4 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
              <Label className="text-sm font-medium text-indigo-800 flex items-center gap-2">
                <FileSpreadsheet size={18} />
                Upload Turf Packet (PDF)
              </Label>
              <p className="text-xs text-indigo-600 mt-1 mb-3">
                Upload a PDF turf packet (NGP VAN format) to extract canvassing addresses
              </p>
              <div className="flex items-center gap-3">
                <label className={`flex items-center gap-2 px-4 py-2 text-white rounded-lg cursor-pointer ${processingPdf ? 'bg-indigo-400' : 'bg-indigo-600 hover:bg-indigo-700'}`}>
                  <Upload size={16} />
                  <span className="text-sm font-medium">{processingPdf ? 'Processing...' : 'Choose PDF'}</span>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleCanvassingPdfUpload}
                    className="hidden"
                    disabled={processingPdf}
                    data-testid="canvassing-pdf-input"
                  />
                </label>
                {processingPdf && (
                  <div className="flex items-center gap-2">
                    <Loader2 size={20} className="animate-spin text-indigo-600" />
                    <span className="text-sm text-indigo-600">Analyzing PDF...</span>
                  </div>
                )}
              </div>
              
              {/* Turf Info Display */}
              {canvassingTurfInfo && (
                <div className="mt-4 p-3 bg-white rounded-lg border border-indigo-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-indigo-100 text-indigo-700">Turf Packet Analyzed</Badge>
                    {uploadedAddresses.length > 0 && (
                      <Badge className="bg-green-100 text-green-700">
                        {uploadedAddresses.length} addresses extracted
                      </Badge>
                    )}
                  </div>
                  {canvassingTurfInfo.turf_name && (
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">Turf:</span> {canvassingTurfInfo.turf_name}
                    </p>
                  )}
                  {canvassingTurfInfo.doors > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      Original door count from packet: {canvassingTurfInfo.doors}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Polygon Zone (Houses/Apartments) */}
          {currentDeliveryType?.zoneType === 'polygon' && (
            <>
              {/* Multi-Zone Controls */}
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <Button
                  type="button"
                  onClick={() => {
                    if (radiusDrawMode) handleRadiusCancel();
                    setDrawingMode(!drawingMode);
                  }}
                  className={drawingMode ? 'bg-green-600 hover:bg-green-700' : 'bg-purple-600 hover:bg-purple-700'}
                  data-testid="start-drawing-zone-btn"
                >
                  {drawingMode ? 'Drawing Mode: ON' : 'Start Drawing Zone'}
                </Button>

                {/* Radius Draw — click center, move to size, click to finalize.
                    Disabled while polygon drawing is active so the two click
                    handlers don't conflict on the same map. */}
                <Button
                  type="button"
                  onClick={handleRadiusModeToggle}
                  disabled={drawingMode}
                  className={
                    radiusDrawMode
                      ? 'bg-amber-600 hover:bg-amber-700'
                      : 'bg-sky-600 hover:bg-sky-700'
                  }
                  data-testid="radius-draw-btn"
                  title={
                    drawingMode
                      ? 'Stop polygon drawing first'
                      : 'Drop a center on the map, move the mouse to size the mile radius, click again to finalize'
                  }
                >
                  <CircleIcon size={16} className="mr-1" />
                  {radiusDrawMode ? 'Cancel Radius Draw' : 'Radius Draw'}
                </Button>

                {/* Status chip for radius-draw. The actual mile-count is
                    rendered on the MAP (next to the cursor) — keeping the
                    live miles off this parent avoids re-renders that would
                    bring back the freezing bug. Text switches based on
                    whether the user has dropped the center yet. */}
                {radiusDrawMode && (
                  <div
                    className="flex items-center gap-2 px-3 py-1.5 bg-sky-50 border border-sky-200 rounded-lg text-xs text-sky-800"
                    data-testid={radiusCenterPlaced ? 'radius-draw-readout' : 'radius-draw-prompt'}
                  >
                    {radiusCenterPlaced ? (
                      <>
                        <span className="font-medium">Sizing radius —</span>
                        <span className="text-sky-700">move the mouse, click to finalize, Esc to cancel</span>
                      </>
                    ) : (
                      <span className="font-medium">Click the map to drop the center of your circle</span>
                    )}
                  </div>
                )}

                {/* Draw Exclusion Zone — same UX as Start Drawing Zone but
                    routes points into `exclusionPolygons` and renders red.
                    Used when a customer marks a sub-area (apartment block,
                    school, etc.) as no-delivery. Disabled while another
                    drawing mode is active to keep map clicks unambiguous. */}
                <Button
                  type="button"
                  onClick={() => {
                    // Toggle exclusion-draw mode. Cancel the in-progress
                    // exclusion line so the next session starts clean.
                    setExclusionDrawingMode(prev => {
                      const next = !prev;
                      if (!next) setExclusionPoints([]); // turning off
                      else {
                        setDrawingMode(false);            // turning on
                        setRadiusDrawMode(false);
                      }
                      return next;
                    });
                  }}
                  disabled={drawingMode || radiusDrawMode}
                  className={
                    exclusionDrawingMode
                      ? 'bg-red-700 hover:bg-red-800'
                      : 'bg-red-600 hover:bg-red-700'
                  }
                  data-testid="draw-exclusion-btn"
                  title={
                    drawingMode || radiusDrawMode
                      ? 'Stop the current drawing mode first'
                      : 'Draw a red polygon INSIDE a delivery zone to exclude those houses from the count'
                  }
                >
                  <Trash2 size={16} className="mr-1" />
                  {exclusionDrawingMode ? 'Drawing Exclusion…' : 'Draw Exclusion Zone'}
                </Button>

                {/* Status chip for exclusion draw + a Cancel button. */}
                {exclusionDrawingMode && (
                  <div
                    className="flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-lg text-xs text-red-800"
                    data-testid="exclusion-draw-prompt"
                  >
                    {exclusionPoints.length >= 3 ? (
                      <>
                        <span className="font-medium">{exclusionPoints.length} points placed —</span>
                        <span>click the first vertex (green) to close, or keep clicking to add more</span>
                      </>
                    ) : (
                      <span className="font-medium">
                        Click inside a delivery zone to place exclusion vertices (need at least 3)
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setExclusionPoints([]);
                        setExclusionDrawingMode(false);
                      }}
                      className="ml-2 text-red-700 underline hover:text-red-900"
                      data-testid="exclusion-draw-cancel-btn"
                    >
                      Cancel
                    </button>
                  </div>
                )}
                
                {/* Drawing Instructions */}
                {drawingMode && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg text-xs text-green-700">
                    <span className="font-medium">
                      {boundaryPoints.length === 0 && 'Click on the map to start drawing'}
                      {boundaryPoints.length === 1 && 'Click to add more points'}
                      {boundaryPoints.length === 2 && 'Add at least one more point'}
                      {boundaryPoints.length >= 3 && (
                        <>Click the <span className="inline-flex items-center justify-center w-5 h-5 bg-green-500 text-white rounded-full text-[10px] font-bold mx-1">✓</span> first point to close, or keep adding points</>
                      )}
                    </span>
                  </div>
                )}
                
                {polygons.some(p => p.length > 0) && (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={addNewPolygon}
                      className="text-blue-600 border-blue-300 hover:bg-blue-50"
                    >
                      <Plus size={16} className="mr-1" /> Add Zone
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={clearAllPolygons}
                      className="text-red-600 border-red-300 hover:bg-red-50"
                    >
                      <Trash2 size={16} className="mr-1" /> Clear All
                    </Button>
                  </>
                )}
              </div>

              {/* Zone List - Shows when multiple zones exist */}
              {polygons.length > 1 && (
                <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-2 font-medium">Zones ({getValidPolygonCount()} valid of {polygons.length})</p>
                  <div className="flex flex-wrap gap-2">
                    {polygons.map((polygon, idx) => (
                      <div
                        key={idx}
                        className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs cursor-pointer transition-all ${
                          idx === activePolygonIndex 
                            ? 'bg-purple-600 text-white' 
                            : polygon.length >= 3 
                              ? 'bg-purple-100 text-purple-700 hover:bg-purple-200' 
                              : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                        }`}
                        onClick={() => selectPolygon(idx)}
                      >
                        <span>Zone {idx + 1}</span>
                        <span className="opacity-75">({polygon.length} pts)</span>
                        {polygon.length >= 3 && <CheckCircle size={12} />}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deletePolygon(idx);
                          }}
                          className="ml-1 hover:text-red-500"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="h-[700px] rounded-xl overflow-hidden border-2 border-dashed border-purple-300 relative" ref={mapContainerRef}>
                {/* House count overlay */}
                <div className="absolute top-3 left-3 z-[1000] bg-white/95 backdrop-blur-sm rounded-lg shadow-lg p-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                      <Home size={20} className="text-purple-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">
                        {newJob.delivery_type === 'apartments' ? 'Apartment Units' : 'Houses'}
                        {getValidPolygonCount() > 1 && ` (${getValidPolygonCount()} zones)`}
                      </p>
                      <p className="text-lg font-bold text-purple-600" data-testid="house-count-display">{houseCount !== null ? houseCount.toLocaleString() : '—'}</p>
                      <div className="flex flex-wrap items-center gap-1 mt-0.5">
                        {dataSource && (
                          <span
                            data-testid="data-source-badge"
                            className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                              dataSource === 'parcel_db' ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' :
                              dataSource === 'osm' ? 'bg-orange-100 text-orange-800 border border-orange-300' :
                              dataSource === 'manual' ? 'bg-blue-100 text-blue-800 border border-blue-300' :
                              dataSource === 'ai_verified' ? 'bg-purple-100 text-purple-800 border border-purple-300' :
                              dataSource === 'google_verified' ? 'bg-cyan-100 text-cyan-800 border border-cyan-300' :
                              dataSource === 'uploaded' ? 'bg-indigo-100 text-indigo-800 border border-indigo-300' :
                              dataSource === 'cloned' ? 'bg-slate-100 text-slate-800 border border-slate-300' :
                              'bg-gray-100 text-gray-700 border border-gray-300'
                            }`}
                            title="Where this house count came from"
                          >
                            Source:&nbsp;
                            {dataSource === 'parcel_db' ? 'Parcel DB' :
                             dataSource === 'osm' ? 'OSM' :
                             dataSource === 'manual' ? 'Manual' :
                             dataSource === 'ai_verified' ? 'AI Verified' :
                             dataSource === 'google_verified' ? 'Google API' :
                             dataSource === 'uploaded' ? 'Uploaded' :
                             dataSource === 'cloned' ? 'Cloned Job' :
                             dataSource}
                          </span>
                        )}
                        {countConfidence && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            countConfidence === 'high' ? 'bg-green-100 text-green-700' :
                            countConfidence === 'manual' ? 'bg-blue-100 text-blue-700' :
                            countConfidence === 'ai_verified' ? 'bg-purple-100 text-purple-700' :
                            countConfidence === 'google_verified' ? 'bg-cyan-100 text-cyan-700' :
                            countConfidence === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                            countConfidence === 'low' ? 'bg-orange-100 text-orange-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {countConfidence === 'high' ? '✓ Verified' :
                             countConfidence === 'manual' ? '✎ Manual' :
                             countConfidence === 'ai_verified' ? '✨ AI Verified' :
                             countConfidence === 'google_verified' ? '🗺️ Google API' :
                             countConfidence === 'low' ? '⚠ Low confidence' :
                             '⚠ Estimated'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {needsGrid && gridSections.length > 1 && (
                  <div className="absolute top-3 right-3 bg-green-600 text-white px-3 py-1.5 rounded-full text-xs font-medium z-[1000]">
                    {gridSections.length} Sections
                  </div>
                )}

                {/* Optimized Google Maps */}
                <DeliveryZoneMap
                  initialCenter={{ lat: mapCenter[0], lng: mapCenter[1] }}
                  initialZoom={14}
                  isDrawing={drawingMode}
                  zonePoints={boundaryPoints}
                  allPolygons={polygons}
                  activePolygonIndex={activePolygonIndex}
                  onDeletePolygon={deletePolygon}
                  centerTrigger={mapCenterTrigger}
                  gridSections={gridSections}
                  showGrids={!hideGridsForPDF && gridSections.length > 0}
                  houseLocations={countedHouseLocations}
                  radiusDrawMode={radiusDrawMode}
                  onRadiusSetCenter={() => setRadiusCenterPlaced(true)}
                  onRadiusFinalize={(lat, lng, meters) => handleRadiusFinalize(lat, lng, meters)}
                  exclusionDrawingMode={exclusionDrawingMode}
                  exclusionPoints={exclusionPoints}
                  exclusionPolygons={exclusionPolygons}
                  onExclusionAddPoint={(lat, lng) => {
                    setExclusionPoints(prev => [...prev, [lat, lng]]);
                  }}
                  onCloseExclusionPolygon={() => {
                    if (exclusionPoints.length >= 3) {
                      setExclusionPolygons(prev => [...prev, exclusionPoints]);
                      setExclusionPoints([]);
                      setExclusionDrawingMode(false);
                      toast.success(`Exclusion zone added (${exclusionPoints.length} points). Houses inside it will be subtracted from the count.`);
                    }
                  }}
                  onDeleteExclusionPolygon={(idx) => {
                    setExclusionPolygons(prev => prev.filter((_, i) => i !== idx));
                    toast.info(`Exclusion zone ${idx + 1} removed`);
                  }}
                  onAddPoint={(lat, lng) => {
                    if (drawingMode) {
                      const currentPoints = boundaryPoints;
                      
                      // Check if clicking near the first point to close the polygon
                      if (currentPoints.length >= 3) {
                        const firstPoint = currentPoints[0];
                        const distance = Math.sqrt(
                          Math.pow(lat - firstPoint[0], 2) + 
                          Math.pow(lng - firstPoint[1], 2)
                        );
                        
                        // If within ~50 meters (roughly 0.0005 degrees), close the polygon
                        if (distance < 0.0005) {
                          // Don't add the point - just turn off drawing mode to "close" the polygon
                          setDrawingMode(false);
                          toast.success(`Polygon closed with ${currentPoints.length} points!`);
                          return;
                        }
                      }
                      
                      // Otherwise, add the new point
                      setBoundaryPoints(prev => [...prev, [lat, lng]]);
                    }
                  }}
                  onClosePolygon={() => {
                    // Called when user clicks directly on the green checkmark marker
                    if (drawingMode && boundaryPoints.length >= 3) {
                      setDrawingMode(false);
                      toast.success(`Polygon closed with ${boundaryPoints.length} points!`);
                    }
                  }}
                  onDragPoint={(index, lat, lng) => {
                    setBoundaryPoints(prev => {
                      const newPoints = [...prev];
                      newPoints[index] = [lat, lng];
                      return newPoints;
                    });
                  }}
                  onInsertPoint={(afterIndex, position) => {
                    handleInsertPoint(afterIndex, position);
                  }}
                />
              </div>

              {/* Alternative Counting Methods — always visible once a polygon
                  is drawn, so admins can pick OSM / Google / Manual / CSV
                  even before running Parcel Data Mgmt count. */}
              {getValidPolygonCount() > 0 && (
                <div className="mt-3 space-y-3">
                  {/* Expand/collapse — auto-open when current count has low
                      confidence OR when no count has been computed yet. */}
                  <details className="group" open={houseCount === null || countConfidence === 'low' || countConfidence === 'estimated'} data-testid="alt-counting-methods">
                    <summary className="flex items-center justify-between cursor-pointer p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="flex items-center gap-2">
                        <Settings size={16} className="text-gray-500" />
                        <span className="text-sm font-medium text-gray-700">Alternative Counting Methods</span>
                        {(houseCount === null || countConfidence === 'low' || countConfidence === 'estimated') && (
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Available</span>
                        )}
                      </div>
                      <ChevronDown size={16} className="text-gray-400 group-open:rotate-180 transition-transform" />
                    </summary>
                    
                    <div className="mt-3 space-y-3">
                      {/* OSM (OpenStreetMap) House Count */}
                      <div className="p-4 bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Globe size={20} className="text-orange-600" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-semibold text-orange-900">OpenStreetMap Count</p>
                              <Badge className="bg-orange-100 text-orange-700 text-[10px]">Alternative</Badge>
                            </div>
                            <p className="text-xs text-orange-700 mt-1">
                              Uses OpenStreetMap building data. Good backup when parcel data coverage is limited.
                            </p>
                            
                            <div className="mt-3">
                              <Button
                                type="button"
                                size="sm"
                                onClick={handleEnhancedHouseSearch}
                                disabled={searchingEnhanced || boundaryPoints.length < 3}
                                className="bg-orange-600 hover:bg-orange-700 text-white"
                                data-testid="osm-search-btn"
                              >
                                {searchingEnhanced ? (
                                  <><Loader2 size={14} className="mr-1 animate-spin" /> Searching OSM...</>
                                ) : (
                                  <><Globe size={14} className="mr-1" /> Count with OSM</>
                                )}
                              </Button>
                            </div>
                            
                            {/* OSM Search Result */}
                            {enhancedSearchResult && (
                              <div className="mt-3 p-3 bg-white border border-orange-200 rounded-lg">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="text-sm font-semibold text-gray-900">
                                      Found: <span className="text-orange-600">
                                        {enhancedSearchResult.house_count} {newJob.delivery_type === 'apartments' ? 'locations' : 'houses'}
                                      </span>
                                    </p>
                                    <p className="text-xs text-gray-600 mt-0.5">
                                      Source: OpenStreetMap
                                    </p>
                                  </div>
                                  {enhancedSearchResult.house_count > 0 && (
                                    <div className="flex gap-2">
                                      <Button
                                        type="button"
                                        size="sm"
                                        onClick={handleApplyEnhancedCount}
                                        className="bg-green-600 hover:bg-green-700 text-white"
                                        data-testid="apply-osm-count-btn"
                                      >
                                        <Check size={14} className="mr-1" /> Apply
                                      </Button>
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => setEnhancedSearchResult(null)}
                                        className="text-gray-500"
                                      >
                                        <X size={14} />
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Google API Search Option */}
                      <div className="p-4 bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-lg">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <MapPin size={20} className="text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-blue-900">Use Google Maps API</p>
                            <p className="text-xs text-blue-700 mt-1">
                              Search for houses using Google Maps Geocoding API. Best for areas with limited OSM data.
                            </p>
                            
                            <div className="mt-3">
                              <Button
                                type="button"
                                size="sm"
                                onClick={handleGoogleApiSearch}
                                disabled={searchingGoogleApi || boundaryPoints.length < 3}
                                className="bg-blue-600 hover:bg-blue-700 text-white"
                                data-testid="use-google-api-btn"
                              >
                                {searchingGoogleApi ? (
                                  <><Loader2 size={14} className="mr-1 animate-spin" /> Searching...</>
                                ) : (
                                  <><Search size={14} className="mr-1" /> Search with Google API</>
                                )}
                              </Button>
                            </div>
                            
                            {/* Google API Result */}
                            {googleApiResult && (
                              <div className="mt-3 p-3 bg-white border border-blue-200 rounded-lg">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="text-sm font-semibold text-gray-900">
                                      Google Estimated: <span className="text-blue-600">
                                        {googleApiResult.house_count} {newJob.delivery_type === 'apartments' ? 'locations' : 'houses'}
                                      </span>
                                    </p>
                                    {googleApiResult.found_count && googleApiResult.found_count !== googleApiResult.house_count && (
                                      <p className="text-xs text-gray-600 mt-0.5">
                                        Sampled {googleApiResult.found_count} addresses, extrapolated to {googleApiResult.house_count}
                                      </p>
                                    )}
                                    <p className="text-xs text-gray-500 mt-0.5">
                                      API Usage: {googleApiResult.api_usage?.geocoding_used || googleApiResult.api_usage?.places_nearby_used || 0} / {googleApiResult.api_usage?.geocoding_limit || googleApiResult.api_usage?.places_nearby_limit || 9000} 
                                      <span className="text-green-600 ml-1">({googleApiResult.api_usage?.remaining || 0} remaining)</span>
                                    </p>
                                  </div>
                                  {googleApiResult.house_count > 0 && (
                                    <div className="flex gap-2">
                                      <Button
                                        type="button"
                                        size="sm"
                                        onClick={handleApplyGoogleCount}
                                        className="bg-green-600 hover:bg-green-700 text-white"
                                        data-testid="apply-google-count-btn"
                                      >
                                        <Check size={14} className="mr-1" /> Apply
                                      </Button>
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => setGoogleApiResult(null)}
                                        className="text-gray-500"
                                      >
                                        <X size={14} />
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* AI Satellite Auto-Capture Option */}
                      <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Camera size={20} className="text-purple-600" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-purple-900">AI Satellite Image Analysis</p>
                            <p className="text-xs text-purple-700 mt-1">
                              Automatically capture satellite imagery of the zone and count houses with AI.
                            </p>
                            
                            <div className="mt-3 flex items-center gap-3">
                              <Button
                                type="button"
                                size="sm"
                                onClick={handleAutoCaptureSatellite}
                                disabled={capturingSatellite || boundaryPoints.length < 3}
                                className="bg-purple-600 hover:bg-purple-700 text-white"
                                data-testid="auto-capture-satellite-btn"
                              >
                                {capturingSatellite ? (
                                  <><Loader2 size={14} className="mr-1 animate-spin" /> Capturing & Analyzing...</>
                                ) : (
                                  <><Sparkles size={14} className="mr-1" /> Auto-Capture & Count</>
                                )}
                              </Button>
                              
                              {/* Optional manual upload */}
                              <label className="cursor-pointer">
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={handleImageUpload}
                                  disabled={analyzingImage}
                                  ref={imageInputRef}
                                />
                                <span className="text-xs text-purple-600 hover:text-purple-800 underline">
                                  or upload manually
                                </span>
                              </label>
                            </div>
                            
                            {/* Satellite Image Preview */}
                            {satelliteImage && (
                              <div className="mt-3">
                                <p className="text-xs text-gray-600 mb-1">Captured satellite image:</p>
                                <img 
                                  src={satelliteImage} 
                                  alt="Captured satellite view" 
                                  className="w-full max-w-xs rounded-lg border border-purple-200 shadow-sm"
                                />
                              </div>
                            )}
                            
                            {/* AI Analysis Result */}
                            {aiAnalysisResult && (
                              <div className="mt-3 p-3 bg-white border border-purple-200 rounded-lg">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="text-sm font-semibold text-gray-900">
                                      AI Count: <span className="text-purple-600">{aiAnalysisResult.house_count} houses</span>
                                    </p>
                                    <p className="text-xs text-gray-600 mt-0.5">
                                      Confidence: <span className={`font-medium ${
                                        aiAnalysisResult.confidence === 'high' ? 'text-green-600' : 
                                        aiAnalysisResult.confidence === 'medium' ? 'text-amber-600' : 'text-red-600'
                                      }`}>{aiAnalysisResult.confidence}</span>
                                    </p>
                                    {aiAnalysisResult.notes && (
                                      <p className="text-xs text-gray-500 mt-1">{aiAnalysisResult.notes}</p>
                                    )}
                                  </div>
                                  <div className="flex gap-2">
                                    <Button
                                      type="button"
                                      size="sm"
                                      onClick={handleApplyAiCount}
                                      className="bg-green-600 hover:bg-green-700 text-white"
                                    >
                                      <Check size={14} className="mr-1" /> Apply
                                    </Button>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => {
                                        setAiAnalysisResult(null);
                                        setSatelliteImage(null);
                                      }}
                                      className="text-gray-500"
                                    >
                                      <X size={14} />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Manual Entry Option */}
                      <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Edit3 size={20} className="text-amber-600" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-amber-900">Manual Count</p>
                            <p className="text-xs text-amber-700 mt-1">Enter a known house count directly.</p>
                            
                            <div className="mt-3 flex items-center gap-2">
                              <Input
                                type="number"
                                min="1"
                                placeholder="Enter count"
                                value={manualHouseCount}
                                onChange={(e) => setManualHouseCount(e.target.value)}
                                className="w-32"
                                data-testid="manual-count-input"
                              />
                              <Button
                                type="button"
                                size="sm"
                                onClick={handleApplyManualCount}
                                disabled={!manualHouseCount || parseInt(manualHouseCount) < 1}
                                className="bg-amber-600 hover:bg-amber-700 text-white"
                                data-testid="apply-manual-count-btn"
                              >
                                Apply
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </details>
                </div>
              )}

              {/* Manual count applied indicator */}
              {countConfidence === 'manual' && houseCount !== null && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Edit3 size={18} className="text-blue-600" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-blue-800">Using manual count: {houseCount} houses</p>
                      <button
                        type="button"
                        onClick={() => {
                          setUseManualCount(false);
                          setCountConfidence(null);
                          setDataSource(null);
                          setManualHouseCount('');
                          setAiAnalysisResult(null);
                          toast.info('Manual count cleared. Click "Count Houses" to re-calculate.');
                        }}
                        className="text-xs text-blue-600 hover:text-blue-800 underline mt-1"
                      >
                        Clear manual count
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* AI-verified count indicator */}
              {countConfidence === 'ai_verified' && houseCount !== null && (
                <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Sparkles size={18} className="text-purple-600" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-purple-800">Using AI-verified count: {houseCount} houses</p>
                      <button
                        type="button"
                        onClick={() => {
                          setUseManualCount(false);
                          setCountConfidence(null);
                          setDataSource(null);
                          setManualHouseCount('');
                          setAiAnalysisResult(null);
                          toast.info('AI count cleared. Click "Count Houses" to re-calculate.');
                        }}
                        className="text-xs text-purple-600 hover:text-purple-800 underline mt-1"
                      >
                        Clear AI count
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Google API verified count indicator */}
              {countConfidence === 'google_verified' && houseCount !== null && (
                <div className="mt-3 p-3 bg-cyan-50 border border-cyan-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <MapPin size={18} className="text-cyan-600" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-cyan-800">Using Google API count: {houseCount} houses</p>
                      <button
                        type="button"
                        onClick={() => {
                          setUseManualCount(false);
                          setCountConfidence(null);
                          setDataSource(null);
                          setManualHouseCount('');
                          setGoogleApiResult(null);
                          toast.info('Google API count cleared. Click "Count Houses" to re-calculate.');
                        }}
                        className="text-xs text-cyan-600 hover:text-cyan-800 underline mt-1"
                      >
                        Clear Google API count
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between mt-3">
                <p className="text-xs text-gray-500">
                  {getValidPolygonCount() === 0 
                    ? 'Draw at least 3 points to create a zone' 
                    : getValidPolygonCount() === 1 
                      ? `${getTotalPoints()} points drawn in 1 zone`
                      : `${getTotalPoints()} points drawn across ${getValidPolygonCount()} zones`
                  }
                </p>

                {/* Use Legacy Parcel Data toggle */}
                <label
                  className="flex items-center gap-2 px-3 py-2 bg-amber-50 rounded-lg border border-amber-200 cursor-pointer select-none"
                  title="When checked, Count Houses queries the legacy 5M+ parcel collection (usa_parcels). When unchecked (default), it queries the new clean-slate Parcel Data Mgmt V2 per-county collections."
                  data-testid="use-legacy-parcel-data-toggle-label"
                >
                  <input
                    type="checkbox"
                    checked={useLegacyParcelData}
                    onChange={(e) => setUseLegacyParcelData(e.target.checked)}
                    className="h-3.5 w-3.5 rounded border-amber-400 text-amber-600 focus:ring-amber-400"
                    data-testid="use-legacy-parcel-data-checkbox"
                  />
                  <span className="text-xs font-medium text-amber-900">Use Legacy Parcel Data</span>
                </label>

                {/* Active data source indicator */}
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg border border-blue-200" title={useLegacyParcelData ? 'Legacy mode: queries the usa_parcels collection. Auto-Heal on the Legacy Parcel Data page if results are empty.' : 'V2 mode: queries the new parcels_v2_<county> per-county collections. Upload data on Admin → Parcel Data Mgmt.'}>
                  <Database size={14} className="text-blue-600" />
                  <span className="text-xs font-medium text-blue-800" data-testid="parcel-db-status">
                    {useLegacyParcelData ? 'Legacy DB' : 'Parcel V2'}
                    {(() => {
                      if (useLegacyParcelData) {
                        const configTotal = parcelDbStatus?._total?.total_parcels || 0;
                        const rawTotal = parcelDbStatus?._total?.raw_collection_count || 0;
                        const displayTotal = Math.max(configTotal, rawTotal);
                        return displayTotal > 0 ? (
                          <span className="text-blue-500 ml-1">
                            ({displayTotal >= 1000 ? `${(displayTotal / 1000).toFixed(0)}K` : displayTotal} parcels)
                          </span>
                        ) : (
                          <span className="text-red-500 ml-1">(no data)</span>
                        );
                      }
                      return <span className="text-blue-500 ml-1">(manual upload)</span>;
                    })()}
                  </span>
                </div>
                
                <Button
                  type="button"
                  variant="outline"
                  onClick={countAddressesInBoundary}
                  disabled={getValidPolygonCount() === 0 || countingHouses}
                  className="border-purple-300 text-purple-600"
                  data-testid="count-houses-btn"
                >
                  {countingHouses ? <><Loader2 size={16} className="mr-2 animate-spin" /> Counting ({useLegacyParcelData ? 'Legacy' : 'V2'})...</> : <><Database size={16} className="mr-2" /> Count {newJob.delivery_type === 'apartments' ? 'Units' : 'Houses'} ({useLegacyParcelData ? 'Legacy' : 'V2'})</>}
                </Button>
                {houseCount !== null && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      clearHouseCountCache();
                      countAddressesInBoundary();
                    }}
                    disabled={countingHouses}
                    className="ml-2 text-xs text-gray-500 hover:text-gray-700"
                    title="Clear cache and recount from fresh data"
                  >
                    <RefreshCw size={14} className="mr-1" /> Recount
                  </Button>
                )}
                {/* Exclusion badge — shows when the last count subtracted
                    houses inside red exclusion zones, OR when exclusion
                    polygons exist on the map (so admins know to re-count
                    after drawing one). */}
                {(excludedHouseCount > 0 || exclusionPolygons.length > 0) && (
                  <div
                    className="ml-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-100 border border-red-300 text-xs text-red-800"
                    data-testid="exclusion-badge"
                    title="Houses inside red exclusion zones are subtracted from the count"
                  >
                    <Trash2 size={12} />
                    {excludedHouseCount > 0
                      ? `${excludedHouseCount.toLocaleString()} excluded`
                      : `${exclusionPolygons.length} exclusion zone${exclusionPolygons.length === 1 ? '' : 's'} — recount`}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Circle Zone (Cars/Hands) */}
          {currentDeliveryType?.zoneType === 'circle' && (
            <>
              <div className="mb-4">
                <Label className="text-sm font-medium text-orange-900">Radius (miles):</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0.1"
                  max="10"
                  value={circleRadius}
                  onChange={(e) => setCircleRadius(parseFloat(e.target.value) || 1)}
                  className="mt-1"
                />
              </div>

              <div className="h-[700px] rounded-xl overflow-hidden border-2 border-dashed border-orange-300">
                <MapContainer center={mapCenter} zoom={13} style={{ height: '100%', width: '100%' }}>
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  <MapClickHandler onMapClick={handleMapClick} onCircleClick={handleCircleClick} isDrawing={false} isCircleMode={true} />
                  <MapCenterUpdater center={mapCenter} key={mapCenterTrigger} />

                  {circleCenter && <CircleBoundsFitter center={circleCenter} radiusMiles={circleRadius} />}
                  {circleCenter && (
                    <>
                      <Circle center={circleCenter} radius={circleRadius * 1609.34} pathOptions={{ color: '#f97316', fillColor: '#f97316', fillOpacity: 0.2 }} />
                      <Marker 
                        position={circleCenter} 
                        icon={centerPointIcon}
                        draggable={true}
                        eventHandlers={{
                          dragend: (e) => {
                            const newPos = e.target.getLatLng();
                            setCircleCenter([newPos.lat, newPos.lng]);
                            setNewJob(prev => ({ ...prev, location: `${newPos.lat.toFixed(4)}, ${newPos.lng.toFixed(4)}` }));
                          }
                        }}
                      />
                    </>
                  )}
                </MapContainer>
              </div>

              <p className="text-xs text-gray-500 mt-2">Click on the map to set the center point</p>

              <div className="mt-4">
                <Label className="text-sm font-medium">Number of Deliveries</Label>
                <Input
                  type="number"
                  min="1"
                  value={newJob.deliveries}
                  onChange={(e) => setNewJob({ ...newJob, deliveries: parseInt(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
            </>
          )}

          {/* Points Zone (Address Specific / Canvassing) */}
          {currentDeliveryType?.zoneType === 'points' && uploadedAddresses.length > 0 && (
            <>
              <div className="h-[700px] rounded-xl overflow-hidden border-2 border-dashed border-indigo-300 relative" data-testid="points-map-container">
                {/* Address count overlay */}
                <div className="absolute top-3 left-3 z-[1000] bg-white/95 backdrop-blur-sm rounded-lg shadow-lg p-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 ${newJob.delivery_type === 'canvassing' ? 'bg-indigo-100' : 'bg-blue-100'} rounded-full flex items-center justify-center`}>
                      <MapPin size={20} className={newJob.delivery_type === 'canvassing' ? 'text-indigo-600' : 'text-blue-600'} />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">{newJob.delivery_type === 'canvassing' ? 'Canvassing Addresses' : 'Target Addresses'}</p>
                      <p className={`text-lg font-bold ${newJob.delivery_type === 'canvassing' ? 'text-indigo-600' : 'text-blue-600'}`}>
                        {uploadedAddresses.length}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Grid sections overlay */}
                {gridSections.length > 1 && (
                  <div className="absolute top-3 right-3 bg-purple-600 text-white px-3 py-1.5 rounded-full text-xs font-medium z-[1000]">
                    {gridSections.length} Grid Sections
                  </div>
                )}

                {/* Show map with markers OR address list if no coordinates */}
                {uploadedAddresses.some(a => a.lat && a.lng) ? (
                  <MapContainer 
                    center={
                      uploadedAddresses.find(a => a.lat && a.lng)
                        ? [uploadedAddresses.find(a => a.lat && a.lng).lat, uploadedAddresses.find(a => a.lat && a.lng).lng]
                        : mapCenter
                    } 
                    zoom={14} 
                    style={{ height: '100%', width: '100%' }}
                  >
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    <MapCenterUpdater center={mapCenter} key={mapCenterTrigger} />
                    
                    {/* Show address markers with coordinates */}
                    {uploadedAddresses.filter(a => a.lat && a.lng).map((addr, idx) => (
                      <Marker 
                        key={idx} 
                        position={[addr.lat, addr.lng]} 
                        icon={new L.DivIcon({
                          className: 'address-marker',
                          html: `<div style="
                            width: 28px;
                            height: 28px;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            background: ${newJob.delivery_type === 'canvassing' ? '#4f46e5' : '#2563eb'};
                            color: white;
                            border-radius: 50%;
                            font-size: 14px;
                            box-shadow: 0 2px 4px rgba(0,0,0,0.3);
                            border: 2px solid white;
                          ">🏠</div>`,
                          iconSize: [28, 28],
                          iconAnchor: [14, 14],
                        })}
                      >
                        <Tooltip direction="top" offset={[0, -14]}>
                          <div className="text-xs max-w-xs">
                            <p className="font-medium">#{idx + 1}</p>
                            {addr.address && <p className="text-gray-600">{addr.address}</p>}
                          </div>
                        </Tooltip>
                      </Marker>
                    ))}
                    
                    {/* Grid section rectangles overlay */}
                    {gridSections.length > 1 && gridSections.map((grid, gIdx) => {
                      const colors = ['#7c3aed', '#2563eb', '#059669', '#d97706', '#dc2626', '#0891b2', '#7c2d12', '#4338ca'];
                      const color = colors[gIdx % colors.length];
                      return (
                        <React.Fragment key={`grid-${gIdx}`}>
                          <Rectangle
                            bounds={[[grid.boundary[0][0], grid.boundary[0][1]], [grid.boundary[2][0], grid.boundary[2][1]]]}
                            pathOptions={{ color, weight: 2, fillOpacity: 0.1, dashArray: '6 3' }}
                          />
                          <Marker
                            position={grid.center}
                            icon={createGridLabelIcon(grid.label, grid.houseCount)}
                          />
                        </React.Fragment>
                      );
                    })}
                  </MapContainer>
                ) : (
                  /* Show address list when no coordinates available */
                  <div className="h-full bg-gradient-to-br from-gray-50 to-gray-100 p-4 pt-16 overflow-y-auto">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {uploadedAddresses.map((addr, idx) => (
                        <div 
                          key={idx} 
                          className={`flex items-center gap-2 p-2 rounded-lg ${
                            newJob.delivery_type === 'canvassing' 
                              ? 'bg-indigo-50 border border-indigo-200' 
                              : 'bg-blue-50 border border-blue-200'
                          }`}
                        >
                          <div className={`w-8 h-8 flex items-center justify-center rounded-full text-white text-sm ${
                            newJob.delivery_type === 'canvassing' ? 'bg-indigo-500' : 'bg-blue-500'
                          }`}>
                            🏠
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-gray-700 truncate">
                              {addr.address}
                            </p>
                          </div>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                            newJob.delivery_type === 'canvassing' ? 'bg-indigo-100 text-indigo-600' : 'bg-blue-100 text-blue-600'
                          }`}>
                            #{idx + 1}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Message for addresses without coordinates */}
              {uploadedAddresses.filter(a => !a.lat || !a.lng).length > 0 && (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle size={18} className="text-amber-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-amber-800">
                        {uploadedAddresses.filter(a => !a.lat || !a.lng).length} addresses will be mapped after job creation
                      </p>
                      <p className="text-xs text-amber-700 mt-1">
                        Addresses will be geocoded to display on the map. You can proceed to create the job.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {uploadedAddresses.filter(a => a.lat && a.lng).length > 500 && (
                <p className="text-xs text-gray-500 mt-2">
                  Showing first 500 of {uploadedAddresses.filter(a => a.lat && a.lng).length} mapped addresses
                </p>
              )}
            </>
          )}
        </Card>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap justify-between gap-4 mt-6">
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => setShowDraftsModal(true)}
            className="border-blue-300 text-blue-600 hover:bg-blue-50"
          >
            <FolderOpen size={18} className="mr-2" /> Load Draft ({savedDrafts.length})
          </Button>
          <Button 
            variant="outline" 
            onClick={handleSaveDraft}
            disabled={savingDraft || (boundaryPoints.length < 3 && !circleCenter && uploadedAddresses.length === 0)}
            className="border-green-300 text-green-600 hover:bg-green-50"
            data-testid="save-draft-btn"
          >
            {savingDraft ? (
              <><Loader2 size={18} className="mr-2 animate-spin" /> Saving...</>
            ) : (
              <><Save size={18} className="mr-2" /> Save Draft</>
            )}
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={resetForm}>
            Reset Form
          </Button>
          
          <Button
            onClick={handleCreateJob}
            disabled={creatingJobs}
            className="bg-purple-600 hover:bg-purple-700 text-white px-8"
            data-testid="create-job-submit-btn"
          >
            {creatingJobs ? (
              <><Loader2 size={18} className="mr-2 animate-spin" /> Creating...</>
            ) : (
              <><Plus size={18} className="mr-2" /> Create Job</>
            )}
          </Button>
        </div>
      </div>

      {/* Drafts Modal */}
      {showDraftsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="font-semibold text-lg">Saved Drafts</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowDraftsModal(false)}>
                <X size={18} />
              </Button>
            </div>
            <div className="p-4 overflow-y-auto max-h-96">
              {savedDrafts.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No saved drafts yet</p>
              ) : (
                <div className="space-y-3">
                  {savedDrafts.map((draft) => (
                    <div key={draft.id} className="p-3 border rounded-lg hover:bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{draft.name}</p>
                          <p className="text-sm text-gray-500">
                            {draft.delivery_type} • {draft.house_count || 0} addresses
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            Saved: {new Date(draft.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleLoadDraft(draft)}
                            className="text-blue-600"
                          >
                            Load
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteDraft(draft.id)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </div>
      )}
      <p className="text-right text-xs text-gray-400 mt-4 pr-2"></p>
    </div>
  );
};

export default function AdminCreateJobWithErrorBoundary() {
  return (
    <CreateJobErrorBoundary>
      <AdminCreateJob />
    </CreateJobErrorBoundary>
  );
}
